// IEA Site Survey Map page (pass 1, doors closed). #/survey/{site}/map
// Words: IEA_STEP3_MAP_WORDS.md (approved 2026-10-15). Mockup: MOCKUP_IEA_NODE_MAP.html v7.
// Saves only Connections rows, through the Outbox (survey-sync.ts): on the iPad first, then the cloud.
// Fold state and Recent live on this device only, per site.

import "./survey-map.css";
import "./survey-cards.css";
import "./survey-drawing.css";
import { drawingRow, GRID, labelWidth, layoutDrawing, nearestSide, placedFrom, sideFields, sidesFrom, type BoxSides, type DrawFeed, type Drawing, type Side } from "./survey-drawing";
import { dotAt, drawingPaper, drawingScreen, viewTransform, type DrawingScreen, type DrawView } from "./survey-drawing-screens";
import { oneLine, type OlKind, type OneLine } from "./survey-oneline";
import { oneLineScreen, type OlDetail } from "./survey-oneline-screens";
import { assetHref } from "./asset-page";
import { cardFor as clCardFor, hasValue as clHasValue, recOf as clRecOf, rowRec as clRowRec, rowsOf as clRowsOf, shown as clShown, wireOf as clWireOf, wireStatus as clWireStatus, wireTag as clWireTag, type Rec } from "./survey-collect";
import { feedBelow } from "./survey-collect-screens";
import { gearKindFor } from "./survey-gear-screens";
import {
  closeSheet as clCloseSheet,
  closeGear as clCloseGear,
  gearSheet,
  gearAsset,
  gearForKey,
  openGear as clOpenGear,
  pickBucket as clPickBucket,
  setGearView as clSetGearView,
  setCircuits as clSetCircuits,
  addBreaker as clAddBreaker,
  setPoles as clSetPoles,
  setSlot as clSetSlot,
  collectDecor,
  collectInit,
  collectSelected,
  collectSheet,
  collectSite,
  photoHooks,
  takePhotos as clTakePhotos,
  jump as clJump,
  openCard as clOpenCard,
  togglePhotos as clTogglePhotos,
  openRow as clOpenRow,
  openWire as clOpenWire,
  openWireLink as clOpenWireLink,
  openWorkList as clOpenWorkList,
  photoRun as clPhotoRun,
  quickPick as clQuickPick,
  sameAbove as clSameAbove,
  sameAsLastWire as clSameAsLastWire,
  markLooked as clMarkLooked,
  setField as clSetField,
  setLoadHp as clSetLoadHp,
  setOpened as clSetOpened,
  setReason as clSetReason,
  setTab as clSetTab,
  setWire as clSetWire,
  setWireType as clSetWireType,
  shootOne as clShootOne,
  submitOther as clSubmitOther,
  toggleNotVisible as clToggleNotVisible,
  cardsParts as clCardsParts,
  moveWire as clMoveWire,
} from "./survey-collect-page";
import { cardsScreen, drawingCard, type CardsSheet, type CardsView } from "./survey-cards-screens";
import { cardLight, cardLine, cardRooms, downstream, feedInto, feederChoices, feederPoints, feedWords, roomOfAsset, takesFeed, walkBreakers, type CardRoom } from "./survey-cards";
import { loadSaved as loadWalkHere } from "./field-labels-page";
import { classesOf } from "./asset-id";
import type { FieldSite } from "./field-labels-site";
import { collectTarget as phCollectTarget, openRow as phOpenRow, photosDecor, photosInit, photosSelected } from "./survey-photos-page";
import {
  askDelete as pvAskDelete,
  cancelDelete as pvCancelDelete,
  confirmDelete as pvConfirmDelete,
  done as pvDone,
  isOpen as pvIsOpen,
  open as pvOpen,
  photoStoreInit,
  relabel as pvRelabel,
  retake as pvRetake,
  retry as pvRetry,
  share as pvShare,
  step as pvStep,
  storeSheet,
  undoDelete as pvUndo,
} from "./survey-photo-store";
import type { SignInClaims } from "./badge";
import { deviceList } from "./outbox";
import { onOutboxChange, saveRecord } from "./survey-sync";
import { readModelTag } from "./firebase";
import { assignTags, mergeTags, parseTagId, positionId, prefixesFrom, tagFromRow, tagId, tagName, tagRow, typesForAsset, wantedTags, type Prefixes, type TagRecord } from "./survey-tags";
import { voltsWords, type MapWarnLine, type SitePack } from "./survey";
import {
  active,
  assetKey,
  buildTree,
  cleanPoint,
  connectionId,
  editRow,
  edited,
  firstToPoint,
  gaps,
  isSource,
  isPositionPoint,
  isTransfer,
  linkFromPack,
  linkStart,
  loadKey,
  mergeLinks,
  newRow,
  nextHow,
  nextLoadNumber,
  nextQuestion,
  nextStep,
  nodeLabel,
  openFeeds,
  outPoint,
  placedSet,
  placeOf,
  pointKey,
  pushRecent,
  Registry,
  roomKey,
  roomOfNode,
  roomWords,
  shortName,
  sourceKey,
  spliceOut,
  type Draft,
  type MapLink,
  type MapTree,
  type NewLink,
  type Stamp,
  undoRemove,
} from "./survey-map";
import {
  assetOpts,
  breakerForm,
  emptyOpts,
  gapList,
  loadForm,
  mapBar,
  mapScreen,
  mapWarn,
  noteSheet,
  fieldFindFoot,
  roomTint,
  opt,
  roomOpts,
  sheet,
  toast,
  undoToast,
  CHAIN_MAX,
  CHAIN_MIN,
  chainStep,
  type MapMode,
  type MapView,
  type SheetTab,
} from "./survey-map-screens";

export interface MapHost {
  root: HTMLElement;
  badge: SignInClaims;
  frame: (above: string, inner: string) => void;
  deviceName: () => string;
  online: () => boolean;
  waiting: () => number;
  large: () => boolean;
  /** The one amber warning line under the header (IEA_SURVEY_ENTRY_WORDS.md section 4). */
  warn?: () => MapWarnLine | null;
  /** The site pack is downloading (Refresh Site Pack on the line). */
  packBusy?: () => boolean;
}

type Sheet =
  | { kind: "next"; tab: string }
  | { kind: "insert"; below: string; tab: string }
  | { kind: "feeds"; asset: string }
  | { kind: "separate"; point: string }
  | { kind: "second"; asset: string }
  | { kind: "gaps" }
  | { kind: "note"; title: string; text: string; room?: string | null };

interface SiteState {
  pack: SitePack;
  reg: Registry;
  links: MapLink[];
  drafts: Draft[];
  cur: string | null;
  focus: boolean;
  /** Loads On: loads hang beside their housing (Gear View step 4). Remembered per site on this iPad. */
  loads: boolean;
  fold: Set<string>;
  recent: string[];
  scale: number;
  sheet: Sheet | null;
  loadType: "Motor" | "Non-motor";
  toast: string;
  loaded: boolean;
  /** Map (pass 1), Collect (pass 2) or Photos (admins). Remembered per site on this iPad. */
  mode: MapMode;
  /** Model Tags given at this site: the cloud's, then this iPad's (AF_MAPPING_WORDS.md section 5). */
  tags: TagRecord[];
  /** Collect on the room cards or the diagram. Remembered per site on this iPad. */
  collectView: "cards" | "diagram" | "oneline";
  /** The room cards: the room you are in, the card open, the breaker or bucket open on it, the sheet up. */
  cards: { room: string | null; open: string | null; row: string | null; sheet: CardsSheet | null };
  /** The Drawing (SURVEY_DRAWING_GRID_C1_WORDS.md): where the paper sits and the label picked. Null view: Fit on open. */
  draw: {
    view: DrawView | null;
    /** Locked: the label whose card is open on the left. */
    sel: string | null;
    /** Build is for arranging, Locked is for collecting (BUILD_LOCK_WORDS.md). Locked when the site opens; holds until Done. */
    build: boolean;
    /** Build: the line picked, gold with a grip on both ends (item 8). */
    line: string | null;
    trayOpen: Set<string> | null;
    /** Labels moved by hand, in grid squares: the pack's, then this iPad's saves (section H). */
    placed: Map<string, { x: number; y: number }>;
    /** The label held in a drag right now, where it sits under the finger. */
    drag: { id: string; x: number; y: number } | null;
    /**
     * A line being drawn from a dot (or an end moved by its grip): the box it starts from, the side, the finger. Drawing
     * units. End "out": the leaving end is the one moving, so the line stays on the fed box (BUILD_LOCK_WORDS.md item 8).
     */
    wire: { from: string; side: Side; x: number; y: number; grip?: string; end?: "out" } | null;
    /** A line let go on gear, waiting on the position to leave from (item 19). P or E on a transfer switch. */
    ask: { from: string; to: string; side: "" | "E"; out?: Side; into?: Side } | null;
    /** The sides picked for the lines into each box (DRAWING_CONNECTORS_WORDS.md item 7). */
    sides: Map<string, BoxSides>;
    /** The One-Line item picked: "n:{Asset ID}", "pd:{link}", "cbl:{link}". */
    olSel: string | null;
    /** Where each paper sat when you left it, so coming back from Cards lands there (item 26). */
    views: { diagram?: DrawView; oneline?: DrawView };
  };
}

let host: MapHost | null = null;
let st: SiteState | null = null;
let wired = false;
let toastTimer: ReturnType<typeof setTimeout> | null = null;
/** The 6-second Undo (CANCEL_WORDS.md section 3): what it says and what Undo does. One at a time. */
let undoNow: { text: string; run: () => void } | null = null;
let undoTimer: ReturnType<typeof setTimeout> | null = null;
export const UNDO_MS = 6000;

/** Offers Undo for 6 seconds at the bottom of the screen. The change is already saved; Undo saves it back. */
function offerUndo(text: string, run: () => void): void {
  if (undoTimer) clearTimeout(undoTimer);
  if (st) st.toast = "";
  undoNow = { text, run };
  undoTimer = setTimeout(() => {
    undoNow = null;
    paint();
  }, UNDO_MS);
  paint();
}

function takeUndo(): void {
  const u = undoNow;
  if (undoTimer) clearTimeout(undoTimer);
  undoNow = null;
  u?.run();
  paint();
}
let lastScrolledTo = "";

// ---- This device ----

const memKey = (company: string, site: string) => `st_survey_map_${company}_${site}`;

function remembered(company: string, site: string): { fold: string[]; recent: string[]; focus: boolean; mode: MapMode; loads: boolean; collectView: "cards" | "diagram" | "oneline"; room: string | null } {
  try {
    const raw = window.localStorage.getItem(memKey(company, site));
    if (raw) {
      const v = JSON.parse(raw) as { fold?: string[]; recent?: string[]; focus?: boolean; mode?: string; loads?: boolean; collectView?: string; room?: string };
      return { fold: v.fold ?? [], recent: v.recent ?? [], focus: v.focus !== false, mode: v.mode === "collect" || v.mode === "photos" ? v.mode : "map", loads: v.loads === true, collectView: v.collectView === "diagram" || v.collectView === "oneline" ? v.collectView : "cards", room: typeof v.room === "string" ? v.room : null };
    }
  } catch {
    // Nothing remembered. Start fresh.
  }
  return { fold: [], recent: [], focus: true, mode: "map", loads: false, collectView: "cards", room: null };
}

function remember(): void {
  if (!st || !host) return;
  try {
    window.localStorage.setItem(memKey(host.badge.company, st.pack.siteNumber), JSON.stringify({ fold: [...st.fold], recent: st.recent, focus: st.focus, mode: st.mode, loads: st.loads, collectView: st.collectView, room: st.cards.room }));
  } catch {
    // Storage full or blocked. The map still works; folds are not remembered.
  }
}

// ---- Open ----

/**
 * The Asset ID page made, changed or removed an asset in the pack. The Map indexes the pack's assets
 * once, so it builds the index again and the new ID is ready to place.
 */
export function mapAssetsChanged(): void {
  if (!st) return;
  st.reg = new Registry(st.pack.assets);
  treeCache = null;
}

/** The room the Map is working in: the room the open sheet asks about, else the selected node's. */
function roomHere(): string | null {
  if (!st) return null;
  if (st.sheet?.kind === "next") {
    const step = nextStep(st.reg, tree(), st.cur);
    if (step && "room" in step && typeof step.room === "string") return step.room;
  }
  return roomOfNode(tree(), st.cur);
}

export function drawMap(h: MapHost, pack: SitePack): void {
  host = h;
  wire();
  if (!st || st.pack !== pack) {
    const mem = remembered(h.badge.company, pack.siteNumber);
    st = {
      pack,
      reg: new Registry(pack.assets),
      links: pack.links.map(linkFromPack),
      drafts: [],
      cur: null,
      focus: mem.focus,
      loads: mem.loads,
      fold: new Set(mem.fold),
      recent: mem.recent,
      scale: 1,
      sheet: null,
      loadType: "Motor",
      toast: "",
      loaded: false,
      mode: mem.mode === "photos" && !photosOk() ? "map" : mem.mode,
      tags: (pack.modelTags ?? []).map(tagFromRow).filter((t): t is TagRecord => t !== null),
      collectView: mem.collectView,
      cards: { room: mem.room, open: null, row: null, sheet: null },
      draw: { view: null, sel: null, build: false, line: null, trayOpen: null, placed: placedFrom(pack.drawing ?? []), drag: null, wire: null, ask: null, sides: sidesFrom(pack.drawing ?? []), olSel: null, views: {} },
    };
    void loadLocal();
  }
  paint();
}

/** Saves made on this iPad lie over the downloaded pack, so a refresh never hides them. */
async function loadLocal(): Promise<void> {
  if (!st || !host) return;
  const prefix = `companies/${host.badge.company}/sites/${st.pack.siteNumber}/connections/`;
  try {
    const local = (await deviceList.records()).filter((r) => r.path.startsWith(prefix)).sort((a, b) => a.savedAt.localeCompare(b.savedAt));
    st.links = mergeLinks(st.links, local.map((r) => r.data));
    // The Drawing's moves saved on this iPad lay over the pack's, the latest winning (section H).
    const drawPrefix = `companies/${host.badge.company}/sites/${st.pack.siteNumber}/drawing/`;
    const moves = (await deviceList.records()).filter((r) => r.path.startsWith(drawPrefix)).map((r) => r.data);
    if (moves.length) st.draw.placed = placedFrom([...(st.pack.drawing ?? []), ...moves]);
  } catch {
    // No device list (private window). The pack's rows stand.
  }
  st.loaded = true;
  if (!st.cur || !tree().nodes.has(st.cur)) st.cur = st.recent.find((k) => tree().nodes.has(k)) ?? tree().roots[0] ?? null;
  paint();
  // Sites mapped before tags existed get theirs now, in walk order from the utility down.
  void syncTags();
}

// ---- Model Tags (AF_MAPPING_WORDS.md section 5, Robert I10 2026-10-21) ----

let tagsRunning = false;
let tagsAgain = false;
/** When each refused tag was last checked against the cloud. A refusal for another reason is not re-read every pass. */
const refusedChecked = new Map<string, number>();

/**
 * Gives every mapped item that has none its tag, and renumbers a pick another iPad took first.
 * Runs after the map opens, after every link save, and when the Outbox changes. One pass at a time.
 */
async function syncTags(): Promise<void> {
  if (!st || !host || !st.loaded) return;
  if (tagsRunning) {
    tagsAgain = true;
    return;
  }
  tagsRunning = true;
  try {
    do {
      tagsAgain = false;
      await tagsPass();
    } while (tagsAgain);
  } finally {
    tagsRunning = false;
  }
}

async function tagsPass(): Promise<void> {
  const s0 = st!;
  const h = host!;
  const site = s0.pack.siteNumber;
  const prefix = `companies/${h.badge.company}/sites/${site}/modelTags/`;
  let local: Awaited<ReturnType<typeof deviceList.records>> = [];
  try {
    local = (await deviceList.records()).filter((r) => r.path.startsWith(prefix));
  } catch {
    // No device list (private window). Tags are given in memory only and never sent.
  }
  const cloud = (s0.pack.modelTags ?? []).map(tagFromRow).filter((t): t is TagRecord => t !== null);
  const used = local.map((r) => r.path.slice(prefix.length));
  const renumbered: string[] = [];
  // A refused pick: if another iPad holds that number for another item, this iPad gives it up.
  for (const r of local.filter((x) => x.state === "refused")) {
    const mine = tagFromRow(r.data);
    if (!mine || Date.now() - (refusedChecked.get(r.path) ?? 0) < 60_000) continue;
    refusedChecked.set(r.path, Date.now());
    const theirs = await readModelTag(h.badge.company, site, tagId(mine));
    const owner = theirs ? tagFromRow(theirs) : null;
    if (!owner) continue;
    cloud.push(owner);
    if (owner.key !== mine.key) {
      await deviceList.deleteRecord(r.path).catch(() => undefined);
      renumbered.push(tagId(mine));
    } else {
      // The cloud already has this exact tag: it was sent, the reply was lost.
      await deviceList.putRecord({ ...r, state: "sent", reason: "", sentAt: new Date().toISOString() }).catch(() => undefined);
    }
  }
  const kept = local.filter((x) => !renumbered.includes(x.path.slice(prefix.length))).map((x) => tagFromRow(x.data)).filter((t): t is TagRecord => t !== null);
  const merged = mergeTags(cloud, kept);
  for (const t of merged.lost) {
    // A newer site pack shows the cloud took this number for another item.
    await deviceList.deleteRecord(prefix + tagId(t)).catch(() => undefined);
    renumbered.push(tagId(t));
  }
  if (st !== s0) return;
  const fresh = assignTags(wantedTags(s0.reg, tree(), s0.links), merged.tags, used);
  s0.tags = [...merged.tags, ...fresh];
  const stampNow = stamp();
  const p = sitePrefixes();
  for (const t of fresh) {
    await saveRecord({ path: prefix + tagId(t), company: h.badge.company, siteNumber: site, assetId: t.assetId || tagName(t, p), card: `Model Tag ${tagName(t, p)}`, data: tagRow(t, stampNow), savedAt: stampNow.at });
  }
  if (renumbered.length) {
    const t0 = parseTagId(renumbered[0]!);
    const shown = t0 ? tagName(t0, p) : renumbered[0]!;
    const words = renumbered.length === 1 ? `${shown} was taken by another iPad. That item has the next free number.` : `${renumbered.length} tags were taken by another iPad. Those items have the next free numbers.`;
    await deviceList.addLog(words).catch(() => undefined);
    flash(words);
  }
  if (fresh.length || renumbered.length) paint();
}

/** The tags at this site, for the cards. */
export function siteTags(): readonly TagRecord[] {
  return st?.tags ?? [];
}

/** The prefixes in use: the Lists page's Model Tag Prefixes over today's defaults. */
export function sitePrefixes(): Prefixes {
  return prefixesFrom(st?.pack.fieldLists ?? []);
}

// ---- Figured views ----

let treeCache: { links: MapLink[]; drafts: Draft[]; t: MapTree } | null = null;
function tree(): MapTree {
  if (!st) throw new Error("No map open.");
  if (!treeCache || treeCache.links !== st.links || treeCache.drafts !== st.drafts) treeCache = { links: st.links, drafts: st.drafts, t: buildTree(st.reg, st.links, st.drafts) };
  return treeCache.t;
}

function view(): MapView {
  const s = st!;
  return {
    siteNumber: s.pack.siteNumber,
    siteName: s.pack.siteName,
    reg: s.reg,
    links: s.links,
    tree: tree(),
    cur: s.cur,
    focus: s.focus,
    fold: s.fold,
    recent: s.recent.filter((k) => tree().nodes.has(k)),
    scale: s.scale,
    gaps: gaps(s.reg, s.links),
    online: host!.online(),
    waiting: host!.waiting(),
    large: host!.large(),
    chainScale: chainScale(),
    collect: s.mode === "collect" ? collectDecorNow() : undefined,
    photos: s.mode === "photos" ? photosDecorNow() : undefined,
    photosOk: photosOk(),
    collectView: s.collectView,
    gear: gearWord,
    loadsOn: s.loads,
    warn: host!.warn?.() ?? null,
    packBusy: host!.packBusy?.() ?? false,
  };
}

/** Gear View step 4: a housing with a door draws as one node, with its count. */
function gearWord(assetId: string): { count: number; word: string } | null {
  const s = st!;
  const kind = gearKindFor({ reg: s.reg }, assetId);
  if (!kind) return null;
  const n = s.reg.positionsOf(assetId).length + [...tree().nodes.values()].filter((x) => x.kind === "point" && x.asset === assetId && !s.reg.positionsOf(assetId).includes(x.ref) && x.ref !== "Lugs").length;
  return { count: n, word: kind === "mcc" ? (n === 1 ? "bucket" : "buckets") : n === 1 ? "breaker" : "breakers" };
}

const CHAIN_KEY = "st_survey_chain_scale";

/** The Chain panel's size, remembered on this iPad for every site. */
function chainScale(): number {
  try {
    const v = Number(window.localStorage.getItem(CHAIN_KEY));
    return v >= CHAIN_MIN && v <= CHAIN_MAX ? v : 1;
  } catch {
    return 1;
  }
}

/** Only admins read IEA photos (storage rule 3), so only admins see the Photos button. */
function photosOk(): boolean {
  return Boolean(host?.badge.authorities.includes("admin"));
}

/** Photos reads Collect's records, so Collect opens first. */
function photosDecorNow() {
  initCollect();
  photosInit({ siteNumber: st!.pack.siteNumber, site: collectSite, cur: () => st!.cur, paint });
  return photosDecor();
}

function collectDecorNow() {
  initCollect();
  return collectDecor();
}

/** Collect borrows the Map's tree, links and save path (survey-collect-page.ts). */
function initCollect(): void {
  const s = st!;
  collectInit({
    company: host!.badge.company,
    pack: s.pack,
    reg: s.reg,
    tree,
    links: () => st!.links,
    cur: () => st!.cur,
    select,
    paint,
    flash,
    undo: offerUndo,
    stamp,
    writeLink: write,
    tags: siteTags,
    prefixes: sitePrefixes,
  });
  photoStoreInit({ ...photoHooks, paint });
}

function paint(): void {
  if (!host || !st) return;
  const v = view();
  if (st.mode === "collect" && st.collectView === "cards") return paintCards(v);
  if (st.mode === "collect" && st.collectView === "oneline") return paintOneLine(v);
  if (st.mode === "collect") return paintDrawing(v);
  const { above, diagram } = mapScreen(v, nextQuestion(st.reg, v.tree, st.cur, st.links));
  // Collect's sheet also serves Photos: Take It Now opens the camera through Collect.
  // The photo big view and its Undo toast sit over Photos. Collect draws the cards or the Drawing above.
  const sheetNow = st.mode === "photos" ? storeSheet() || collectSheet() : sheetHtml();
  // The open door (Gear View) sits under the sheets, the camera and the big photo view. Every mode.
  const gear = gearSheet(st.mode, photosOk());
  const keep = keepScroll(["gv-front", "gv-side", "gv-table"]);
  host.frame(above + gear + sheetNow + (undoNow ? undoToast(undoNow.text) : toast(st.toast)), "");
  keep();
  guardBeneath(Boolean(gear));
  // Keep the selected node in view, once per selection, so a fold or a refresh does not jump.
  const canvas = host.root.querySelector<HTMLElement>("#mp-canvas");
  if (canvas && diagram.focusAt && lastScrolledTo !== st.cur) {
    lastScrolledTo = st.cur ?? "";
    canvas.scrollTo({ left: Math.max(0, diagram.focusAt.x - canvas.clientWidth / 2), top: Math.max(0, diagram.focusAt.y - canvas.clientHeight / 3) });
  }
}

// ---- The room cards (SURVEY_CARDS_C1_WORDS.md, MOCKUP_SURVEY_CARDS.html approved 2026-10-29 9:11 PM) ----

/** The walk on this iPad: the cards' hanging order and their drawings come from it. None: the sheet's order. */
function walkHere(): FieldSite {
  const read = (k: string): string | null => {
    try {
      return window.localStorage.getItem(k);
    } catch {
      return null;
    }
  };
  return loadWalkHere(read, host!.badge.company, st!.pack.siteNumber).data;
}

/** The rooms and cards now, with the walk read once. */
function cardsNow(): { rooms: CardRoom[]; walk: FieldSite } {
  const walk = walkHere();
  return { rooms: cardRooms(st!.reg, walk, st!.pack.siteNumber, classesOf(st!.pack)), walk };
}

/** What the cards draw with: the Cards view, and the card open on the Drawing when Locked (BUILD_LOCK_WORDS.md item 13). */
function cardsView(rooms: CardRoom[], walk: FieldSite): CardsView {
  const s = st!;
  const items = rooms.flatMap((r) => r.items);
  const gearOf = (id: string) => items.find((i) => i.assetId === id)?.gear ?? null;
  const parts = clCardsParts();
  const fedRoomsAll = () => [...new Set(s.reg.assets.filter((a) => !s.reg.isPosition(a.assetId)).map(roomOfAsset))].sort((x, y) => x.localeCompare(y, undefined, { numeric: true }));
  return {
    siteNumber: s.pack.siteNumber,
    reg: s.reg,
    site: parts.site,
    rooms,
    room: s.cards.room,
    open: s.cards.open,
    row: s.cards.row,
    sheet: s.cards.sheet,
    panel: parts.panel,
    walkRoom: (room) => walk.rooms.find((r) => r.name === room) ?? null,
    feed: (assetId) => feedInto(s.links, assetId),
    feedWords: (l) => feedWords(s.reg, l, gearOf),
    fedRooms: fedRoomsAll,
    fedGear: (asset, room) => feederChoices(s.reg, tree(), room, asset),
    fedPoints: (feeder) => feederPoints(parts.site, s.reg, feeder, gearOf(feeder)),
  };
}

function paintCards(v: MapView): void {
  const s = st!;
  const h = host!;
  initCollect();
  const { rooms, walk } = cardsNow();
  if (!rooms.some((r) => r.room === s.cards.room)) s.cards.room = rooms[0]?.room ?? null;
  const html = cardsScreen(cardsView(rooms, walk));
  // Collect's sheets (the wire card, the camera, the big photo) sit over the cards as they sit over the diagram.
  const sheetNow = storeSheet() || collectSheet();
  const keep = keepScroll(["sc-right", "sc-left", "sc-opts"]);
  h.frame(mapBar(v) + mapWarn(v) + html + sheetNow + (undoNow ? undoToast(undoNow.text) : toast(st!.toast)), "");
  keep();
  guardBeneath(false);
}

// ---- The Drawing (SURVEY_DRAWING_GRID_C1_WORDS.md, MOCKUP_SURVEY_DRAWING.html, approved 2026-10-30 8:04 AM) ----

/** From Point words that mean the gear itself, not a position on it: no tab. */
const SELF_POINTS = new Set(["", "Out", "Sec"]);

/** The Fed From lines the drawing draws: gear to gear, Primary and Emergency on a transfer switch (item 21). */
function drawFeeds(): DrawFeed[] {
  const s = st!;
  return active(s.links)
    .filter((l) => l.goesTo === "Equipment" && l.toAsset && !isPositionPoint(l.toPoint))
    .map((l) => ({
      id: l.id,
      from: l.fromAsset,
      point: SELF_POINTS.has(l.fromPoint) ? "" : l.fromPoint,
      to: l.toAsset,
      side: isTransfer(s.reg, l.toAsset) ? (l.toPoint === "Emergency" ? "E" : "P") : "",
      notVerified: l.howKnown !== "Seen",
      ...sidesOf(l.toAsset, l.toPoint === "Emergency"),
    }));
}

/** The sides saved for a line into a box: its Fed From, or its Emergency side. Blank: the default. */
function sidesOf(assetId: string, emergency: boolean): { outSide?: Side; inSide?: Side } {
  const v = st!.draw.sides.get(assetId);
  const out = emergency ? v?.emergencyOut : v?.feedOut;
  const into = emergency ? v?.emergencyIn : v?.feedIn;
  return { ...(out ? { outSide: out } : {}), ...(into ? { inSide: into } : {}) };
}

const tapeName = (id: string) => shortName(st!.reg.byId.get(id) ?? { assetId: id, location: "" });

/**
 * The drawing, laid out on the iPad each time from the cards' rooms and the Fed From lines (item 11). Labels moved by
 * hand sit where they were put (item 17); a label held in a drag sits under the finger.
 */
function drawingNow(rooms: CardRoom[]): Drawing {
  const s = st!;
  const placed = new Map(s.draw.placed);
  if (s.draw.drag) placed.set(s.draw.drag.id, { x: s.draw.drag.x, y: s.draw.drag.y });
  return layoutDrawing({
    rooms: rooms.map((r) => ({ room: r.room, ids: r.items.map((i) => i.assetId) })),
    names: new Map(rooms.flatMap((r) => r.items.map((i) => [i.assetId, tapeName(i.assetId)] as const))),
    feeds: drawFeeds(),
    topRoom: "Outside",
    placed,
  });
}

/** Loads fed from an asset (End load links), for the Loads switch on the Drawing (item 9). */
function loadsOf(assetId: string): MapLink[] {
  return active(st!.links).filter((l) => l.goesTo === "End load" && l.fromAsset === assetId);
}

/** Where a line being dragged starts: the dot it left, or, when an end is moved by its grip, the end that stays. */
function rubberStart(d: Drawing, from: { assetId: string; x: number; y: number; w: number }, w: { side: Side; grip?: string; end?: "out" }): { x1: number; y1: number } {
  const line = w.grip ? d.lines.find((l) => l.feed.id === w.grip) : undefined;
  // The leaving end moves: the line hangs from where it goes into the fed box (BUILD_LOCK_WORDS.md item 8).
  if (line && w.end === "out") return { x1: line.x2, y1: line.y2 };
  if (line) return { x1: line.x1, y1: line.y1 };
  const p = dotAt(from, w.side, d.tabs);
  return { x1: p.x, y1: p.y };
}

/** Everything the Drawing's screen draws with, built once per paint (and once a frame in a drag). */
function drawModel(): { screen: DrawingScreen; d: Drawing; sheet: string } {
  const s = st!;
  const { rooms, walk } = cardsNow();
  const items = rooms.flatMap((r) => r.items);
  const itemOf = new Map(items.map((i) => [i.assetId, i]));
  const gearOf = (id: string) => itemOf.get(id)?.gear ?? null;
  const parts = clCardsParts();
  const d = drawingNow(rooms);
  // Locked: the picked label is the card open on the left. Its Done puts the card away and unpicks it (item 13).
  if (s.draw.sel && (!itemOf.has(s.draw.sel) || s.cards.open !== s.draw.sel)) s.draw.sel = null;
  if (s.draw.line && !d.lines.some((l) => l.feed.id === s.draw.line)) s.draw.line = null;
  const open = !s.draw.build && s.draw.sel ? drawingCard(cardsView(rooms, walk), s.draw.sel) : null;
  const cardLoads = open && s.loads ? loadsOf(s.draw.sel!).map((l) => [l.fromPoint, l.endLoadType, l.endLoadHp ? `${l.endLoadHp} HP` : "", l.endLoadTag].filter(Boolean).join(" · ")) : [];
  const rooms0 = rooms.map((r) => r.room);
  const from = s.draw.wire ? d.labels.find((l) => l.assetId === s.draw.wire!.from) : undefined;
  const ask = s.draw.ask;
  const used = ask ? new Set(active(s.links).filter((l) => l.fromAsset === ask.from).map((l) => l.fromPoint)) : new Set<string>();
  const screen: DrawingScreen = {
    d,
    view: s.draw.view ?? { x: 0, y: 0, k: 1 },
    sel: s.draw.build ? null : s.draw.sel,
    build: s.draw.build,
    line: s.draw.build ? s.draw.line : null,
    card: open?.card ?? null,
    cardLoads,
    light: (id) => {
      const item = itemOf.get(id);
      return item ? cardLight(parts.site, item) : "none";
    },
    tag: (feedId) => {
      const w = clWireOf(parts.site, feedId);
      return { text: clWireTag(w), done: clWireStatus(w) !== "none" };
    },
    tint: (room) => (room === "Outside" ? "#f1f3f5" : roomTint(rooms0, room)),
    name: tapeName,
    trayOpen: (room) => (s.draw.trayOpen ??= new Set(s.cards.room ? [s.cards.room] : [])).has(room),
    arrange: s.draw.placed.size > 0 || s.draw.sides.size > 0,
    dragging: s.draw.drag?.id ?? null,
    wire: from && s.draw.wire ? { ...rubberStart(d, from, s.draw.wire), x2: s.draw.wire.x, y2: s.draw.wire.y } : null,
    ask: ask
      ? {
          title: `${tapeName(ask.to)} is fed from which position on ${tapeName(ask.from)}?`,
          sub: ask.side === "E" ? "Emergency side of the transfer switch" : "The same list as Fed From on the card",
          points: feederPoints(parts.site, s.reg, ask.from, gearOf(ask.from)).map((p) => ({ ...p, used: used.has(p.key) })),
        }
      : null,
    loads: s.loads,
    loadCount: (id) => loadsOf(id).length,
  };
  return { screen, d, sheet: open?.sheet ?? "" };
}

function paintDrawing(v: MapView): void {
  const s = st!;
  const h = host!;
  initCollect();
  const { screen, d, sheet } = drawModel();
  // Collect's sheets (the wire card, the camera, the big photo) sit over the drawing as they sit over the cards. The open
  // card's own Fed From sheet comes last.
  const sheetNow = storeSheet() || collectSheet() || sheet;
  const keep = keepScroll(["dw-left", "dw-tray", "sc-opts"]);
  h.frame(mapBar(v) + mapWarn(v) + drawingScreen(screen) + sheetNow + (undoNow ? undoToast(undoNow.text) : toast(s.toast)), "");
  keep();
  guardBeneath(false);
  // First open: Fit. After that the paper stays where you left it, through every redraw (item 26).
  if (!s.draw.view) fitDrawing(d);
}

/** In a drag, only the paper redraws, once a frame, so the label and its lines follow the finger on a big site. */
let dragFrame = 0;
function paintDragFrame(): void {
  if (dragFrame) return;
  dragFrame = requestAnimationFrame(() => {
    dragFrame = 0;
    const g = host?.root.querySelector("#dw-g");
    if (!g || (!st?.draw.drag && !st?.draw.wire)) return;
    g.innerHTML = drawingPaper(drawModel().screen);
  });
}

const drawPath = (assetId: string) => `companies/${host!.badge.company}/sites/${st!.pack.siteNumber}/drawing/${assetId}`;

/** One label's place, saved whole through the Outbox (section H). By hand Yes, or No when Arrange takes it back. */
function saveDrawPlace(assetId: string, x: number, y: number, byHand: boolean): void {
  const s = st!;
  if (byHand) s.draw.placed.set(assetId, { x, y });
  else s.draw.placed.delete(assetId);
  void saveRecord({ path: drawPath(assetId), company: host!.badge.company, siteNumber: s.pack.siteNumber, assetId, card: byHand ? "Drawing move" : "Drawing arrange", data: drawingRow(assetId, x, y, byHand, stamp()), savedAt: new Date().toISOString() });
}

/**
 * A line drawn from one label onto another (items 18 to 22): the first feeds the second. One rule: a line is a Fed
 * From, so this saves exactly what Fed From on the card saves. Gear with positions asks which one first.
 */
function connectDraw(from: string, to: string, out?: Side, into?: Side): void {
  const s = st!;
  if (!from || !to || from === to) return;
  if (!takesFeed(s.reg, to)) {
    flash(`${tapeName(to)} is the source. Nothing feeds it.`);
    return paint();
  }
  // A transfer switch takes two lines: the first is Primary, a line from anywhere else is Emergency (item 21).
  const primary = feedInto(s.links, to);
  const side: "" | "E" = isTransfer(s.reg, to) && primary && primary.fromAsset !== from ? "E" : "";
  const item = cardsNow().rooms.flatMap((r) => r.items).find((i) => i.assetId === from);
  const pts = feederPoints(clCardsParts().site, s.reg, from, item?.gear ?? null);
  if (pts.length) {
    s.draw.ask = { from, to, side, out, into };
    return paint();
  }
  void finishConnect(from, to, selfPoint(from), side, out, into);
}

/** The position picked (or the gear itself): Fed From as the card saves it, or the Emergency side. */
async function finishConnect(from: string, to: string, point: string, side: "" | "E", out?: Side, into?: Side): Promise<void> {
  const s = st!;
  s.draw.ask = null;
  if (side !== "E") {
    await saveFedFrom(to, from, point);
    // The sides go with the line only when it took (a loop is refused): the same line again only moves its ends.
    const now = feedInto(s.links, to);
    if (out && into && now && now.fromAsset === from && now.fromPoint === point) saveSides(to, false, out, into);
    return;
  }
  if (downstream(tree(), to).has(from)) {
    flash(`${tapeName(to)} already feeds ${tapeName(from)}.`);
    return paint();
  }
  const old = active(s.links).find((l) => l.goesTo === "Equipment" && l.toAsset === to && l.toPoint === "Emergency");
  if (old && old.fromAsset === from && old.fromPoint === point) {
    if (out && into) saveSides(to, true, out, into);
    return paint();
  }
  if (point) await breakerReady(from, point);
  // A third line replaces Emergency (item 21).
  if (old) editLink(old.id, { Status: "Removed" }, "Map link moved (Emergency, Drawing)");
  const id = addLink({ fromAsset: from, fromPoint: point, toAsset: to, toPoint: "Emergency", howKnown: "Seen" });
  if (old) await clMoveWire(old.id, id);
  if (out && into) saveSides(to, true, out, into);
  offerUndo(`${tapeName(to)} Emergency side is fed from ${tapeName(from)}.`, () => {
    editLink(id, { Status: "Removed" }, "Map link removed (Undo)");
    if (old) editLink(old.id, { Status: old.status || "Active" }, "Map link put back (Undo)");
  });
}

/** Remove Line (item 23): the Fed From goes, the wire card closes, Undo brings it back. No second window. */
function removeLine(linkId: string): void {
  const s = st!;
  const l = s.links.find((x) => x.id === linkId);
  if (!l) return;
  clCloseSheet();
  editLink(linkId, { Status: "Removed" }, "Map link removed (Drawing)");
  // A transfer switch keeps its other source: name the side that went (item 21).
  const side = isTransfer(s.reg, l.toAsset) ? (l.toPoint === "Emergency" ? " Emergency side" : " Primary side") : "";
  offerUndo(`Line removed: ${tapeName(l.toAsset)}${side} is not connected.`, () => {
    editLink(linkId, { Status: l.status || "Active" }, "Map link put back (Undo)");
  });
}

/**
 * The sides a line into a box leaves and enters (DRAWING_CONNECTORS_WORDS.md item 7), saved on that box's drawing
 * record through the Outbox. The record keeps the box where it sits: by hand if it was moved, else laid out.
 */
function saveSides(assetId: string, emergency: boolean, out: Side | "", into: Side | "", undo = true): void {
  const s = st!;
  const before = s.draw.sides.get(assetId);
  const cur = before ?? { feedOut: "", feedIn: "", emergencyOut: "", emergencyIn: "" };
  // Out the bottom and in the top is the default: saved blank.
  const plain = out === "Bottom" && into === "Top";
  const next: BoxSides = emergency ? { ...cur, emergencyOut: plain ? "" : out, emergencyIn: plain ? "" : into } : { ...cur, feedOut: plain ? "" : out, feedIn: plain ? "" : into };
  writeSides(assetId, next);
  if (undo && before) offerUndo(`${tapeName(assetId)}: line moved.`, () => writeSides(assetId, before));
}

function writeSides(assetId: string, v: BoxSides): void {
  const s = st!;
  if (!v.feedOut && !v.feedIn && !v.emergencyOut && !v.emergencyIn) s.draw.sides.delete(assetId);
  else s.draw.sides.set(assetId, v);
  const hand = s.draw.placed.get(assetId);
  const at = hand ?? (() => {
    const l = drawModel().d.labels.find((x) => x.assetId === assetId);
    return { x: Math.round((l?.x ?? 0) / GRID), y: Math.round((l?.y ?? 0) / GRID) };
  })();
  const data = { ...drawingRow(assetId, at.x, at.y, Boolean(hand), stamp()), ...sideFields(v) };
  void saveRecord({ path: drawPath(assetId), company: host!.badge.company, siteNumber: s.pack.siteNumber, assetId, card: "Drawing sides", data, savedAt: new Date().toISOString() });
  paint();
}

/**
 * A line's end moved by its grip (DRAWING_CONNECTORS_WORDS.md item 5). Onto another side of the same box: it only moves.
 * Onto another box: that box takes this feed, as Fed From does today. Its own old feed goes, the wire comes along, the
 * feeder and the position stay, and Undo puts all of it back.
 */
async function moveEnd(linkId: string, to: string, into: Side): Promise<void> {
  const s = st!;
  const l = s.links.find((x) => x.id === linkId && x.status !== "Removed");
  if (!l) return paint();
  const emergency = l.toPoint === "Emergency";
  const line = drawModel().d.lines.find((x) => x.feed.id === linkId);
  const out = line?.outSide ?? "Bottom";
  if (to === l.toAsset) return saveSides(to, emergency, out, into);
  if (to === l.fromAsset) return paint();
  if (!takesFeed(s.reg, to)) {
    flash(`${tapeName(to)} is the source. Nothing feeds it.`);
    return paint();
  }
  if (downstream(tree(), to).has(l.fromAsset)) {
    flash(`${tapeName(to)} already feeds ${tapeName(l.fromAsset)}.`);
    return paint();
  }
  const prev = emergency ? active(s.links).find((x) => x.goesTo === "Equipment" && x.toAsset === to && x.toPoint === "Emergency") : feedInto(s.links, to);
  editLink(l.id, { Status: "Removed" }, "Map link moved (Drawing, end moved)");
  if (prev) editLink(prev.id, { Status: "Removed" }, "Map link replaced (Drawing, end moved)");
  // The line keeps what is known about it: a Told line stays Told when it is only moved.
  const id = addLink({ fromAsset: l.fromAsset, fromPoint: l.fromPoint, toAsset: to, toPoint: emergency ? "Emergency" : firstToPoint(s.reg, to), howKnown: l.howKnown });
  await clMoveWire(l.id, id);
  saveSides(to, emergency, out, into, false);
  offerUndo(`${tapeName(to)} is fed from ${tapeName(l.fromAsset)}.`, () => {
    editLink(id, { Status: "Removed" }, "Map link removed (Undo)");
    editLink(l.id, { Status: l.status || "Active" }, "Map link put back (Undo)");
    if (prev) editLink(prev.id, { Status: prev.status || "Active" }, "Map link put back (Undo)");
  });
}

/**
 * The leaving end of a line moved by its grip (DRAWING_CONNECTORS_WORDS.md item 5, BUILD_LOCK_WORDS.md item 8). Onto
 * another side of the same feeder: the line only leaves from there. Onto another box: that box becomes the feeder, as
 * Fed From on the card does it (positions asked, a loop refused, Undo shown). A transfer switch's line keeps its side.
 */
function moveStart(linkId: string, from: string, out: Side): void {
  const s = st!;
  const l = s.links.find((x) => x.id === linkId && x.status !== "Removed");
  if (!l) return paint();
  const emergency = l.toPoint === "Emergency";
  const into = drawModel().d.lines.find((x) => x.feed.id === linkId)?.inSide ?? "Top";
  if (from === l.fromAsset) return saveSides(l.toAsset, emergency, out, into);
  if (from === l.toAsset) return paint();
  const side: "" | "E" = emergency ? "E" : "";
  const item = cardsNow().rooms.flatMap((r) => r.items).find((i) => i.assetId === from);
  if (feederPoints(clCardsParts().site, s.reg, from, item?.gear ?? null).length) {
    s.draw.ask = { from, to: l.toAsset, side, out, into };
    return paint();
  }
  void finishConnect(from, l.toAsset, selfPoint(from), side, out, into);
}

/** Arrange (item 14): every hand-moved label goes back to the layout, every line to bottom-to-top. Undo puts them back. */
function arrangeDrawing(): void {
  const s = st!;
  const before = new Map(s.draw.placed);
  const sides = new Map(s.draw.sides);
  if (!before.size && !sides.size) return;
  for (const [id, p] of before) saveDrawPlace(id, p.x, p.y, false);
  for (const id of sides.keys()) writeSides(id, { feedOut: "", feedIn: "", emergencyOut: "", emergencyIn: "" });
  offerUndo("Laid out again from the Fed From lines.", () => {
    for (const [id, p] of before) saveDrawPlace(id, p.x, p.y, true);
    for (const [id, v] of sides) writeSides(id, v);
  });
}

// ---- The One-Line (MOCKUP_IEA_ONE_LINE.html rev 3, approved 2026-10-21): drawn on the Drawing's positions ----

/** What each label is on the one-line, by the same rule that gives it its tags (survey-tags.ts `typesForAsset`). */
function olKind(assetId: string): OlKind {
  const s = st!;
  const a = s.reg.byId.get(assetId);
  const types = typesForAsset(s.reg, assetId, Boolean(a && isSource(a)));
  if (types.includes("Utility")) return "utility";
  if (types.includes("Generator")) return "generator";
  if (types.includes("Transfer Switch")) return "ats";
  if (types.includes("VFD")) return "vfd";
  if (types.includes("Transformer")) return "transformer";
  return "bus";
}

function oneLineNow(): OneLine {
  const s = st!;
  const site = clCardsParts().site;
  const prefixes = sitePrefixes();
  const byKey = new Map(s.tags.map((t) => [t.key, t]));
  const num = (r: Rec, k: string) => (r.f[k] !== undefined && r.f[k] !== "" ? String(r.f[k]) : "");
  return oneLine(drawingNow(cardsNow().rooms), {
    kind: olKind,
    tag: (key) => {
      const t = byKey.get(key);
      return t ? tagName(t, prefixes) : null;
    },
    data: (id) => {
      const a = s.reg.byId.get(id);
      const r = clRecOf(site, id);
      const k = olKind(id);
      if (k === "transformer" || k === "utility") return [num(r, "kVA") && `${num(r, "kVA")} kVA`, num(r, "%Z") && `${num(r, "%Z")} %Z`].filter(Boolean).join(" · ") || (a?.equipmentType ?? "");
      return [a?.equipmentType ?? "", a ? voltsWords(a) : ""].filter(Boolean).join(" · ");
    },
    device: (from, point) => {
      const row = clRowsOf(site, from).find((x) => x.point === point);
      const r = row ? clRowRec(site, from, row) : null;
      const type = r ? num(r, "Device Type") : "";
      const amps = r ? num(r, "Rating (A)") : "";
      return { data: [type, amps && `${amps} A`].filter(Boolean).join(" · "), fuse: /fuse/i.test(type) };
    },
    positionId: (from, point) => positionId(s.reg, from, point),
    wire: (id) => {
      const w = clWireOf(site, id);
      if (clWireStatus(w) === "none") return "";
      return [clWireTag(w), w.length !== "" ? `${w.length} ft` : ""].filter(Boolean).join(" · ");
    },
  });
}

/**
 * A device's unfolding card (ONE_LINE_UNFOLD_WORDS.md section 2): what the asset record holds from the published study,
 * the same fields and words as the asset page. "Waits on the study" until a study is published.
 */
function olDetail(assetId: string): OlDetail {
  const s = st!;
  const a = s.reg.byId.get(assetId);
  const doc = s.pack.assetDocs?.[assetId] ?? {};
  const str = (k: string) => (doc[k] === undefined || doc[k] === null ? "" : String(doc[k]));
  const ie = doc.incidentEnergyCal;
  const afb = doc.arcFlashBoundary;
  const feed = feedInto(s.links, assetId);
  const gearOf = (id: string) => cardsNow().rooms.flatMap((r) => r.items).find((i) => i.assetId === id)?.gear ?? null;
  return {
    type: a?.equipmentType ?? "",
    room: a ? roomOfAsset(a) : "",
    volts: str("nominalVoltage") || (a ? voltsWords(a) : "") || "Not recorded",
    ie: typeof ie === "number" ? `${ie} cal/cm²` : str("incidentEnergyCal") || "Waits on the study",
    afb: typeof afb === "number" ? `${afb} in.` : str("arcFlashBoundary") || "Waits on the study",
    overduty: doc.isOverduty === true,
    ieStatus: str("ieStatus"),
    limited: str("limitedApproach") || "Not recorded",
    restricted: str("restrictedApproach") || "Not recorded",
    fedFrom: feed ? feedWords(s.reg, feed, gearOf) : null,
    fedNotVerified: Boolean(feed && feed.howKnown !== "Seen"),
    study: str("afStudy") || "No study yet",
    assetHref: assetHref(s.pack.siteNumber, assetId),
  };
}

function paintOneLine(v: MapView): void {
  const s = st!;
  const h = host!;
  initCollect();
  const ol = oneLineNow();
  const html = oneLineScreen({ ol, view: s.draw.view ?? { x: 0, y: 0, k: 1 }, sel: s.draw.olSel, short: (id) => id.replace(/^\d{2}-/, ""), detail: olDetail, admin: true });
  const sheetNow = storeSheet() || collectSheet();
  const keep = keepScroll(["dw-left"]);
  h.frame(mapBar(v) + mapWarn(v) + html + sheetNow + (undoNow ? undoToast(undoNow.text) : toast(s.toast)), "");
  keep();
  guardBeneath(false);
  if (!s.draw.view) fitBox(ol.box, 0);
}

/** Fit (item 16): the whole site in view, clear of the tray. */
function fitDrawing(d?: Drawing): void {
  const s = st!;
  const svg = host?.root.querySelector<SVGSVGElement>("#dw-svg");
  if (!svg) return;
  const dd = d ?? drawingNow(cardsNow().rooms);
  const r = svg.getBoundingClientRect();
  const tray = host!.root.querySelector<HTMLElement>(".dw-tray")?.offsetWidth ?? 0;
  void r;
  fitBox(dd.box, tray);
}

/** Puts a box of drawing units in view, clear of a tray on the left. */
function fitBox(box: { x0: number; y0: number; x1: number; y1: number }, tray: number): void {
  const svg = host?.root.querySelector<SVGSVGElement>("#dw-svg");
  if (!svg) return;
  const r = svg.getBoundingClientRect();
  const bw = Math.max(1, box.x1 - box.x0);
  const bh = Math.max(1, box.y1 - box.y0);
  const aw = Math.max(100, r.width - tray - 40);
  const ah = Math.max(100, r.height - 40);
  const k = Math.min(1.6, aw / bw, ah / bh);
  setDrawView({ k, x: tray + 20 + (aw - bw * k) / 2 - box.x0 * k, y: 20 + (ah - bh * k) / 2 - box.y0 * k });
}

/** Moves the paper without a redraw: only the transform changes, so a pan stays smooth on a big site. */
function setDrawView(v: DrawView): void {
  if (!st) return;
  st.draw.view = v;
  host?.root.querySelector("#dw-g")?.setAttribute("transform", viewTransform(v));
}

/** A label tapped on the drawing or in the tray: what was collected shows on the left (item 25). */
/**
 * Locked: a tap on a label opens its card on the left, the same card as Cards (BUILD_LOCK_WORDS.md item 13). It is the
 * card open in Cards too, so switching views keeps your place. Null puts it away.
 */
function drawPick(assetId: string | null): void {
  if (!st) return;
  const c = st.cards;
  st.draw.sel = assetId;
  if (assetId) {
    const room = cardsNow().rooms.find((r) => r.items.some((i) => i.assetId === assetId));
    if (room) c.room = room.room;
    c.open = assetId;
    c.row = null;
    c.sheet = null;
  } else c.open = null;
  // A new card starts at its top; the drawing stays where it is.
  host?.root.querySelector("#dw-left")?.scrollTo?.(0, 0);
  paint();
}

/**
 * Opens the card for an asset, from a scan or the Work List: its room, its card, and the bucket when it is one.
 * A breaker key ("p:PNL:11") opens that breaker. True when the asset has a card here.
 */
function openCardFor(assetId: string, point: string | null = null): boolean {
  const s = st!;
  const housing = s.reg.isPosition(assetId) ? s.reg.parents().find((p) => assetId.startsWith(`${p.assetId}-`) && s.reg.positionsOf(p.assetId).includes(assetId.slice(p.assetId.length + 1))) : undefined;
  const id = housing?.assetId ?? assetId;
  const row = housing ? assetId.slice(housing.assetId.length + 1) : point;
  const room = cardsNow().rooms.find((r) => r.items.some((i) => i.assetId === id));
  if (!room) {
    const a = s.reg.byId.get(id);
    flash(`${a ? shortName(a) : id} has no card here.`);
    return true;
  }
  s.cards = { room: room.room, open: id, row, sheet: null };
  remember();
  paint();
  host?.root.querySelector(`[data-sc-a="${CSS.escape(id)}"]`)?.scrollIntoView({ block: "start" });
  return true;
}

/** A breaker tapped on the drawing is on the panel's record before its card opens, with the walk's poles. */
async function breakerReady(assetId: string, key: string): Promise<void> {
  if (!/^\d+$/.test(key)) return;
  const item = cardsNow().rooms.flatMap((r) => r.items).find((i) => i.assetId === assetId);
  const b = walkBreakers(item?.gear ?? null).find((x) => x.key === key);
  if (!b) return;
  const rec = clCardsParts().site.recs[assetId]?.breakers[key];
  if (rec?.f.Slot === "Breaker") return;
  await clSetField(`B:${assetId}:${key}`, "Slot", "Breaker");
  if (b.poles > 1) await clSetField(`B:${assetId}:${key}`, "Poles", String(b.poles));
}

/**
 * Fed From picked (items 11 to 13): the same Connections row the Map saves. A changed feed removes the old one and
 * carries its wire to the new. A feed from something this asset already feeds would loop, so it is refused.
 */
/** Where a feed leaves gear with no positions: "Out", or "Sec" on a utility (the Map's own words, `outPoint`). */
function selfPoint(assetId: string): string {
  const a = st!.reg.byId.get(assetId);
  return a ? outPoint(a) : "Out";
}

async function saveFedFrom(asset: string, from: string, point: string): Promise<void> {
  const s = st!;
  s.cards.sheet = null;
  const old = feedInto(s.links, asset);
  if (old && old.fromAsset === from && old.fromPoint === point) return paint();
  const name = (id: string) => shortName(s.reg.byId.get(id) ?? { assetId: id, location: "" });
  if (downstream(tree(), asset).has(from)) {
    flash(`${name(asset)} already feeds ${name(from)}. Pick what feeds ${name(asset)}.`);
    return paint();
  }
  if (point) await breakerReady(from, point);
  if (old) editLink(old.id, { Status: "Removed" }, "Map link moved (Fed From on a card)");
  const id = addLink({ fromAsset: from, fromPoint: point, toAsset: asset, toPoint: firstToPoint(s.reg, asset), howKnown: "Seen" });
  if (old) await clMoveWire(old.id, id);
  const words = feedWords(s.reg, { ...(old ?? ({} as MapLink)), id, fromAsset: from, fromPoint: point, toAsset: asset } as MapLink, (x) => cardsNow().rooms.flatMap((r) => r.items).find((i) => i.assetId === x)?.gear ?? null);
  offerUndo(`${name(asset)} is fed from ${words}.`, () => {
    editLink(id, { Status: "Removed" }, "Map link removed (Undo)");
    if (old) editLink(old.id, { Status: old.status || "Active" }, "Map link put back (Undo)");
  });
}

/**
 * Switches Collect's view. The Drawing and the One-Line share the paper but not its scale, so each keeps its own place:
 * leaving one saves where it sat, coming back puts it there. First time in: Fit (words item 26).
 */
function toCollectView(next: "cards" | "diagram" | "oneline"): void {
  const s = st!;
  if (next === s.collectView) return;
  if (s.collectView !== "cards" && s.draw.view) s.draw.views[s.collectView] = s.draw.view;
  if (next !== "cards") s.draw.view = s.draw.views[next] ?? null;
  s.collectView = next;
  remember();
}

/** The room cards' taps: data-cd. Fields, photos, Opened and the wire card keep Collect's own data-cl. */
function cardsTap(el: HTMLElement, ev: Event): void {
  const s = st!;
  const c = s.cards;
  const v = el.dataset.cdV ?? "";
  switch (el.dataset.cd) {
    case "view-cards":
    case "view-diagram":
    case "view-oneline": {
      toCollectView(el.dataset.cd === "view-cards" ? "cards" : el.dataset.cd === "view-oneline" ? "oneline" : "diagram");
      return paint();
    }
    case "sheet-bg":
      if (ev.target !== el) return;
      c.sheet = null;
      return paint();
    case "sheet-close":
      c.sheet = null;
      return paint();
    case "rooms":
      c.sheet = { kind: "rooms" };
      return paint();
    case "room":
      c.room = v;
      c.open = null;
      c.row = null;
      c.sheet = null;
      remember();
      host?.root.querySelector("#sc-right")?.scrollTo({ top: 0 });
      return paint();
    case "open":
      // One card open at a time; a tap on the open card's name puts it away.
      c.open = c.open === v ? null : v;
      c.row = null;
      return paint();
    case "close":
      c.open = null;
      c.row = null;
      return paint();
    case "row": {
      const a = c.open;
      if (!a) return;
      if (c.row === v) {
        c.row = null;
        return paint();
      }
      c.row = v;
      void breakerReady(a, v).then(paint);
      return paint();
    }
    case "fed":
      c.sheet = { kind: "fed", asset: v, room: null, from: null };
      return paint();
    case "fed-room":
      if (c.sheet?.kind === "fed") c.sheet = { ...c.sheet, room: v };
      return paint();
    case "fed-gear": {
      if (c.sheet?.kind !== "fed") return;
      const pts = feederPoints(clCardsParts().site, s.reg, v, cardsNow().rooms.flatMap((r) => r.items).find((i) => i.assetId === v)?.gear ?? null);
      // One way out (a transformer, a disconnect): no position to pick. It leaves from the gear itself, saved as
      // the Map saves it ("Out", "Sec" on a utility). A blank From Point is refused by the Connections rule.
      if (!pts.length) return void saveFedFrom(c.sheet.asset, v, selfPoint(v));
      c.sheet = { ...c.sheet, from: v };
      return paint();
    }
    case "fed-point":
      if (c.sheet?.kind === "fed" && c.sheet.from) void saveFedFrom(c.sheet.asset, c.sheet.from, v);
      return;
    case "fed-back":
      if (c.sheet?.kind === "fed") c.sheet = c.sheet.from ? { ...c.sheet, from: null } : { ...c.sheet, room: null };
      return paint();
  }
}

// ---- The page under the door (bug 2026-10-16: Robert signed out while adding breakers on the iPad) ----
// The door's Done sits over the top bar's SIGN OUT, which signs out on one tap. A quick second tap
// after Done, a pencil double tap, or the iPad keyboard shifting taps on a fixed overlay could land
// on the bar. While the door is open, everything under it is inert and the page does not scroll.
// After Done, the top bar stays inert a moment longer so the second tap of a double tap goes nowhere.

/** How long the top bar ignores taps after the door closes. */
export const DOOR_GRACE_MS = 600;
let doorWasOpen = false;
let graceTimer: ReturnType<typeof setTimeout> | null = null;

function guardBeneath(open: boolean): void {
  const root = host?.root;
  if (!root) return;
  const overlay = (el: Element) => el.matches(".gv-bg, .cl-sheet, .mp-sheet, .pv-view, .pv-confirm, .pv-toast, .mp-toast, .mp-undo, .cl-cam");
  const bar = root.querySelector(".bar");
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
  if (open) {
    for (const el of Array.from(root.children)) if (!overlay(el)) el.setAttribute("inert", "");
  } else if (doorWasOpen && bar) {
    bar.setAttribute("inert", "");
    graceTimer = setTimeout(() => {
      host?.root.querySelector(".bar")?.removeAttribute("inert");
      graceTimer = null;
    }, DOOR_GRACE_MS);
  }
  document.documentElement.classList.toggle("gv-lock", open);
  doorWasOpen = open;
}

/** A repaint rebuilds the screen. Put these scroll spots back where the user left them. */
function keepScroll(ids: string[]): () => void {
  const at = ids.map((id) => {
    const el = host?.root.querySelector<HTMLElement>(`#${id}`);
    return el ? { id, left: el.scrollLeft, top: el.scrollTop } : null;
  });
  return () => {
    for (const a of at) {
      if (!a) continue;
      const el = host?.root.querySelector<HTMLElement>(`#${a.id}`);
      if (el) {
        el.scrollLeft = a.left;
        el.scrollTop = a.top;
      }
    }
  };
}

function flash(text: string): void {
  if (!st) return;
  st.toast = text;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    if (st) st.toast = "";
    paint();
  }, 3400);
}

function select(key: string | null): void {
  if (!st) return;
  st.cur = key;
  if (key) st.recent = pushRecent(st.recent, key);
  remember();
  paint();
}

// ---- Saving ----

function stamp(): Stamp {
  const h = host!;
  return { siteNumber: st!.pack.siteNumber, badgeId: h.badge.badgeId, holderName: h.badge.holderName, deviceName: h.deviceName() || "This device", at: new Date().toISOString() };
}

const pathOf = (id: string) => `companies/${host!.badge.company}/sites/${st!.pack.siteNumber}/connections/${id}`;

/** One write: in memory now, then the device list, then the cloud. An unsent row takes the edit in place. */
async function write(id: string, data: Record<string, unknown>, assetId: string, card: string): Promise<void> {
  if (!st || !host) return;
  st.links = mergeLinks(st.links, [data]);
  const path = pathOf(id);
  let full = data;
  try {
    const prev = (await deviceList.records()).find((r) => r.path === path) ?? null;
    full = edited(prev?.data ?? null, prev?.state === "sent", data);
  } catch {
    // No device list. Send as is.
  }
  await saveRecord({ path, company: host.badge.company, siteNumber: st.pack.siteNumber, assetId, card, data: full, savedAt: new Date().toISOString() });
  // A new item on the map takes its tag the moment it is saved.
  void syncTags();
}

/** A new link, or a removed one brought back with the same ID. */
function addLink(l: NewLink): string {
  const s = stamp();
  const id = connectionId(l.fromAsset, l.fromPoint, l.load ? "" : l.toAsset, nextLoadNumber(st!.links, l.fromAsset, l.fromPoint));
  void write(id, newRow(l, id, s), l.fromAsset, l.load ? "Map link to a load" : "Map link");
  return id;
}

function editLink(id: string, changes: Record<string, unknown>, card: string): void {
  const l = st!.links.find((x) => x.id === id);
  void write(id, editRow(id, changes, stamp()), l?.fromAsset ?? "", card);
}

// ---- The picker ----

function sheetHtml(): string {
  if (!st || !st.sheet) return "";
  const s = st.sheet;
  const t = tree();
  const placed = placedSet(st.links);
  if (s.kind === "note") return noteSheet(s.title, s.text, s.room !== undefined ? fieldFindFoot(s.room) : "");
  if (s.kind === "gaps") return sheet("Not on the tree yet", "Assets in the registry with no feed mapped. Positions count on their housing, not here.", [{ id: "g", name: "By room", body: gapList(gaps(st.reg, st.links)) }], "g", "Scan any of these to place it.");
  if (s.kind === "second") {
    const body = st.reg.sources().map((a) => opt("pick-second", a.assetId, shortName(a), a.equipmentType)).join("") || emptyOpts("No sources in the registry.");
    return sheet(`Add Second Source to ${shortName(st.reg.byId.get(s.asset) ?? { assetId: s.asset, location: "" })}`, "A transfer switch has two ways in. Pick the other source.", [{ id: "s", name: "Sources", body }], "s", "It draws in orange.");
  }
  if (s.kind === "feeds" || s.kind === "separate") {
    const asset = s.kind === "feeds" ? s.asset : t.nodes.get(s.point)!.asset;
    const a = st.reg.byId.get(asset)!;
    const room = placeOf(a).room;
    const feeds = room ? openFeeds(st.reg, t, room, asset) : [];
    const roomsDone = new Set<string>();
    const body = feeds.length
      ? feeds
          .filter((n) => (n.kind === "room" ? !roomsDone.has(n.key) && roomsDone.add(n.key) : true))
          .map((n) => opt("pick-feed", n.key, n.kind === "room" ? `Room ${n.ref}` : n.kind === "point" ? `${shortName(st!.reg.byId.get(n.asset) ?? { assetId: n.asset, location: "" })} ${n.ref}` : nodeLabel(st!.reg, n, st!.links), n.kind === "room" ? "Straight under the room" : n.kind === "point" ? (t.nodes.has(n.key) ? "Position on the tree" : "Position") : "Feeds out, nothing below"))
          .join("")
      : emptyOpts(room ? `No open feeds in ${room} yet. Map the room first, or leave it on the not-mapped list.` : "This asset has no room in the registry. Fix its location in the workbook.");
    const title = s.kind === "feeds" ? `${shortName(a)} is not mapped yet. Pick what feeds it.` : `Fed Separately: what feeds ${shortName(a)} ${t.nodes.get(s.point)!.ref}?`;
    const sub = s.kind === "feeds" ? `${a.equipmentType}${room ? `. Only open feeds in ${room} are shown.` : ""}` : "A bucket fed by its own conductors, not the bus. It stays under its MCC; the feed draws as a side line.";
    return sheet(title, sub, [{ id: "f", name: room ? `Open feeds in ${room}` : "Open feeds", body }], "f", "It snaps into the tree under what you pick. Do it again from there to keep going up.");
  }
  if (s.kind === "insert") {
    const below = t.nodes.get(s.below)!;
    const up = below.parent ? t.nodes.get(below.parent)! : null;
    const room = roomOfNode(t, below.parent);
    const outOnly = (floor: boolean) => (room ? st!.reg.roomParents(room, floor).filter((x) => st!.reg.wayOut(x.assetId) === "out") : []);
    const tabs: SheetTab[] = [
      { id: "floor", name: `Floor (from ${room ?? "?"})`, body: assetOpts(outOnly(true), placed, "pick-insert", `Nothing on the floor fed from ${room ?? "this room"}.`) },
      { id: "here", name: `This Room (${room ?? "?"})`, body: assetOpts(outOnly(false), placed, "pick-insert", `No feed-out assets in ${room ?? "this room"}.`) },
    ];
    return sheet(`Insert between ${up ? nodeLabel(st.reg, up, st.links) : "the source"} and ${nodeLabel(st.reg, below, st.links)}`, "Drop in what you found on the way. The rest of the chain stays attached.", tabs, s.tab, "Pick the asset that sits between them.");
  }
  // Next.
  const step = nextStep(st.reg, t, st.cur);
  const c = st.cur ? t.nodes.get(st.cur) : undefined;
  const name = c ? nodeLabel(st.reg, c, st.links) : "";
  if (!step) return "";
  if (step.kind === "sources") {
    const body = st.reg
      .sources()
      .map((a) => opt("pick-source", a.assetId, shortName(a), `${a.equipmentType}${t.nodes.has(sourceKey(a.assetId)) ? " · on the tree, continue" : ""}`))
      .join("");
    return sheet("New Chain: start at a source", "Utility services and generators.", [{ id: "s", name: "Sources", body: body || emptyOpts("No utility service or generator in the registry.") }], "s", "Or scan the label.");
  }
  if (step.kind === "rooms") return sheet(`Which room does ${name} feed?`, "Rooms, areas and locations. A room can sit under more than one source.", [{ id: "r", name: "Rooms", body: roomOpts(st.reg.rooms(), "pick-room") }], "r", "Or scan the label. Field Find if it is not here.");
  if (step.kind === "roomAssets") {
    return sheet(`Which asset in ${step.room}?`, `Only assets assigned to ${roomWords(step.room)}. No buckets or breakers here.`, [{ id: "p", name: step.room, body: assetOpts(st.reg.roomParents(step.room, false), placed, "pick-asset", `Nothing assigned to ${step.room}.`) }], "p", "Or scan the label. Field Find if it is not here.");
  }
  if (step.kind === "positions") {
    const body = st.reg
      .positionsOf(step.asset)
      .map((p) => opt("pick-point", p, p, t.nodes.has(pointKey(step.asset, p)) ? "On the tree, continue" : "Position"))
      .join("");
    return sheet(`Which position in ${name}?`, "Its positions, from the registry, in order on the gear.", [{ id: "k", name: "Positions", body }], "k", "Or scan the label.");
  }
  if (step.kind === "breaker") return sheet(`Breaker or circuit no. in ${name}?`, "The registry has no positions for this one. Type the number on the breaker or the directory.", [{ id: "b", name: "Breaker", body: breakerForm(step.lugs) }], "b", "Not an asset. It is stored on the link.");
  const room = step.room;
  const tabs: SheetTab[] = [];
  if (room) {
    tabs.push({ id: "here", name: `This Room (${room})`, body: assetOpts(st.reg.roomParents(room, false), placed, "pick-asset", `Nothing else assigned to ${room}.`) });
    tabs.push({ id: "floor", name: `Floor (from ${room})`, body: assetOpts(st.reg.roomParents(room, true), placed, "pick-asset", `Nothing on the floor fed from ${room}.`) });
  }
  tabs.push({ id: "room", name: "Another Room", body: roomOpts(st.reg.rooms(), "pick-room", room) });
  tabs.push({ id: "load", name: "Load", body: loadForm() });
  return sheet(`What does ${name} feed?`, room ? `This room first. Floor holds field gear fed from ${room}.` : "Pick a room, or a load.", tabs, s.tab || tabs[0]!.id, "Or scan the label. Field Find if it is not here.");
}

function openSheet(s: Sheet): void {
  if (!st) return;
  st.sheet = s;
  paint();
}

function closeSheet(): void {
  if (!st) return;
  st.sheet = null;
  paint();
}

// ---- Picks ----

function pickSource(id: string): void {
  if (!st) return;
  const key = sourceKey(id);
  if (!tree().nodes.has(key)) st.drafts = [...st.drafts, { kind: "source", parent: null, ref: id, asset: id }];
  st.sheet = null;
  select(key);
}

function pickRoom(room: string): void {
  if (!st || !st.cur) return;
  const key = roomKey(st.cur, room);
  if (!tree().nodes.has(key)) st.drafts = [...st.drafts, { kind: "room", parent: st.cur, ref: room, asset: "" }];
  st.sheet = null;
  select(key);
}

function pickPoint(asset: string, point: string): void {
  if (!st) return;
  const key = pointKey(asset, point);
  if (!tree().nodes.has(key)) st.drafts = [...st.drafts, { kind: "point", parent: assetKey(asset), ref: point, asset }];
  st.sheet = null;
  select(key);
}

function pickAsset(id: string): void {
  if (!st || !st.cur) return;
  const start = linkStart(st.reg, tree(), st.cur);
  if (!start) return;
  if (placedSet(st.links).has(id)) {
    flash(`${shortName(st.reg.byId.get(id) ?? { assetId: id, location: "" })} is already on the tree. One asset, one feed.`);
    return;
  }
  addLink({ ...start, toAsset: id, toPoint: firstToPoint(st.reg, id), howKnown: "Seen" });
  st.sheet = null;
  select(assetKey(id));
}

function addLoad(): void {
  if (!st || !st.cur || !host) return;
  const start = linkStart(st.reg, tree(), st.cur);
  if (!start) return;
  const val = (id: string) => host!.root.querySelector<HTMLInputElement>(id)?.value ?? "";
  const hp = val("#mp-ldhp").trim();
  const id = addLink({
    ...start,
    toAsset: "",
    toPoint: "",
    howKnown: "Seen",
    load: { type: st.loadType, hp, tag: val("#mp-lde"), description: val("#mp-ldd") },
  });
  st.sheet = null;
  if (st.loadType === "Motor" && !hp) flash("Saved with HP blank. It shows amber on the gap list in Collect.");
  else flash("Load added.");
  select(loadKey(id));
}

/** Insert: the asset that sits between. Old link Removed, two new links, the rest stays attached. */
function pickInsert(id: string): void {
  if (!st || !st.sheet || st.sheet.kind !== "insert") return;
  const t = tree();
  const below = t.nodes.get(st.sheet.below);
  const old = below ? st.links.find((l) => l.id === below.linkId) : undefined;
  if (!below || !old) return;
  const a = st.reg.byId.get(id)!;
  addLink({ fromAsset: old.fromAsset, fromPoint: old.fromPoint, toAsset: id, toPoint: firstToPoint(st.reg, id), howKnown: "Seen" });
  if (old.goesTo === "End load") {
    addLink({
      fromAsset: id,
      fromPoint: outPoint(a),
      toAsset: "",
      toPoint: "",
      howKnown: old.howKnown,
      load: { type: old.endLoadType === "Non-motor" ? "Non-motor" : "Motor", hp: old.endLoadHp, tag: old.endLoadTag, description: old.notes },
    });
  } else {
    addLink({ fromAsset: id, fromPoint: outPoint(a), toAsset: old.toAsset, toPoint: old.toPoint, howKnown: old.howKnown });
  }
  editLink(old.id, { Status: "Removed" }, "Map link removed (Insert)");
  st.sheet = null;
  flash("Inserted. The rest of the chain stays attached below it.");
  select(assetKey(id));
}

/** The point a picked open feed leaves from. A position not yet on the tree is read from its key. */
function startOf(key: string): { fromAsset: string; fromPoint: string } | null {
  if (key.startsWith("p:") && !tree().nodes.has(key)) {
    const rest = key.slice(2);
    const i = rest.lastIndexOf(":");
    return { fromAsset: rest.slice(0, i), fromPoint: rest.slice(i + 1) };
  }
  return linkStart(st!.reg, tree(), key);
}

function pickFeed(key: string): void {
  if (!st || !st.sheet) return;
  const start = startOf(key);
  if (!start) return;
  if (st.sheet.kind === "feeds") {
    const id = st.sheet.asset;
    addLink({ ...start, toAsset: id, toPoint: firstToPoint(st.reg, id), howKnown: "Seen" });
    st.sheet = null;
    flash(`Placed. Keep going down, or scan the next one and pick what feeds it.`);
    select(assetKey(id));
    return;
  }
  if (st.sheet.kind === "separate") {
    const p = tree().nodes.get(st.sheet.point)!;
    addLink({ ...start, toAsset: p.asset, toPoint: p.ref, howKnown: "Seen" });
    st.sheet = null;
    flash(`${p.ref} is fed separately. It stays under its MCC; the feed draws as a side line.`);
    select(p.key);
  }
}

function pickSecond(sourceId: string): void {
  if (!st || !st.sheet || st.sheet.kind !== "second") return;
  const ats = st.sheet.asset;
  const src = st.reg.byId.get(sourceId)!;
  addLink({ fromAsset: sourceId, fromPoint: outPoint(src), toAsset: ats, toPoint: "Emergency", howKnown: "Seen" });
  st.sheet = null;
  flash(`${shortName(st.reg.byId.get(ats) ?? { assetId: ats, location: "" })} now shows two ways in.`);
  select(assetKey(ats));
}

function removeCur(): void {
  if (!st || !st.cur) return;
  const t = tree();
  const n = t.nodes.get(st.cur);
  if (!n) return;
  if ((t.kids.get(n.key) ?? []).length) {
    // Take one asset out of the middle: what is below moves up to the line above.
    const plan = spliceOut(st.reg, t, st.links, n.key);
    if (!plan) {
      flash(`Clear what is below ${nodeLabel(st.reg, n, st.links)} first.`);
      return;
    }
    const before = st.links.map((l) => ({ id: l.id, status: l.status }));
    for (const id of plan.remove) editLink(id, { Status: "Removed" }, "Map link removed");
    const added = plan.relink.map((l) => addLink(l));
    st.recent = st.recent.filter((k) => k !== n.key);
    remember();
    select(n.parent);
    offerUndo(`${nodeLabel(st.reg, n, st.links)} removed. What was below it moved up.`, () => {
      for (const e of undoRemove(before, plan.remove, added)) editLink(e.id, { Status: e.status }, "Map link put back (Undo)");
      select(n.key);
    });
    return;
  }
  const label = nodeLabel(st.reg, n, st.links);
  if (n.draft) {
    const was = st.drafts;
    st.drafts = st.drafts.filter((d) => !((d.kind === "source" && sourceKey(d.ref) === n.key) || (d.kind === "room" && d.parent && roomKey(d.parent, d.ref) === n.key) || (d.kind === "point" && pointKey(d.asset, d.ref) === n.key)));
    st.recent = st.recent.filter((k) => k !== n.key);
    remember();
    select(n.parent);
    offerUndo(`${label} removed.`, () => {
      if (!st) return;
      st.drafts = was;
      remember();
      select(n.key);
    });
    return;
  }
  if (!n.linkId) return;
  const linkId = n.linkId;
  const before = st.links.map((l) => ({ id: l.id, status: l.status }));
  editLink(linkId, { Status: "Removed" }, "Map link removed");
  st.recent = st.recent.filter((k) => k !== n.key);
  remember();
  select(n.parent);
  offerUndo(`${label} removed from the tree. It shows on the not-mapped list again.`, () => {
    for (const e of undoRemove(before, [linkId], [])) editLink(e.id, { Status: e.status }, "Map link put back (Undo)");
    select(n.key);
  });
}

// ---- Scan (the shell's Scan button, on the Map page) ----

/** True when the Map handled the asset. A position opens its housing on the tree. */
export function mapScanned(assetId: string): boolean {
  if (!st) return false;
  const t = tree();
  const a = st.reg.byId.get(assetId);
  if (!a) return false;
  // On the room cards a scan opens the card, and a bucket's QR opens its bucket on it.
  if (st.mode === "collect" && st.collectView === "cards") return openCardFor(assetId);
  // On the Drawing a scan picks the gear (a bucket picks its housing), so what was collected shows on the left.
  if (st.mode === "collect") {
    const housing = st.reg.isPosition(assetId) ? st.reg.parents().find((p) => assetId.startsWith(`${p.assetId}-`) && st!.reg.positionsOf(p.assetId).includes(assetId.slice(p.assetId.length + 1))) : undefined;
    drawPick(housing?.assetId ?? assetId);
    return true;
  }
  if (st.mode === "photos") {
    // Photos: a label opens its node's photos. A position opens its housing's table at that row.
    const housing = st.reg.isPosition(assetId) ? st.reg.parents().find((p) => assetId.startsWith(`${p.assetId}-`) && st!.reg.positionsOf(p.assetId).includes(assetId.slice(p.assetId.length + 1))) : undefined;
    const key = housing ? pointKey(housing.assetId, assetId.slice(housing.assetId.length + 1)) : isSource(a) ? sourceKey(assetId) : assetKey(assetId);
    if (t.nodes.has(key) || (housing && t.nodes.has(assetKey(housing.assetId)))) {
      if (housing && !t.nodes.has(key)) {
        st.drafts = [...st.drafts, { kind: "point", parent: assetKey(housing.assetId), ref: assetId.slice(housing.assetId.length + 1), asset: housing.assetId }];
      }
      select(photosSelected(key));
      return true;
    }
    flash(`${shortName(a)} is not on the map yet. Switch to Map to place it, then come back to Photos.`);
    return true;
  }
  if (isSource(a)) {
    pickSource(assetId);
    return true;
  }
  if (st.reg.isPosition(assetId)) {
    const housing = st.reg.parents().find((p) => assetId.startsWith(`${p.assetId}-`) && st!.reg.positionsOf(p.assetId).includes(assetId.slice(p.assetId.length + 1)));
    if (housing && t.nodes.has(assetKey(housing.assetId))) {
      pickPoint(housing.assetId, assetId.slice(housing.assetId.length + 1));
      return true;
    }
    if (housing) return mapScanned(housing.assetId);
    return false;
  }
  if (placedSet(st.links).has(assetId) && t.nodes.has(assetKey(assetId))) {
    select(assetKey(assetId));
    flash(`${shortName(a)} is on the tree. Selected.`);
    return true;
  }
  openSheet({ kind: "feeds", asset: assetId });
  return true;
}

// ---- Events ----

function wire(): void {
  if (wired || !host) return;
  wired = true;
  // A refused tag (another iPad took the number) is renumbered as soon as the Outbox hears it.
  onOutboxChange(() => {
    if (st && /^#\/survey\/[^/]+\/map/.test(location.hash)) void syncTags();
  });
  const root = host.root;
  const onMap = () => /^#\/survey\/[^/]+\/map/.test(location.hash);
  root.addEventListener("click", (ev) => {
    if (!onMap() || !st) return;
    const el = (ev.target as Element).closest<HTMLElement | SVGElement>("[data-mp]");
    if (!el) return;
    const act = el.dataset.mp;
    if (act === "undo") return takeUndo();
    const k = el.dataset.k ?? "";
    const v = el.dataset.v ?? "";
    if (act === "sheet-bg") {
      if (ev.target === el) closeSheet();
      return;
    }
    ev.stopPropagation();
    switch (act) {
      case "select":
        st.sheet = null;
        // Gear View step 3: a housing tapped in Map or Photos opens its door too. In Map a position
        // stays a plain pick, so the chain work goes on as before.
        if (st.mode !== "collect") {
          const n = tree().nodes.get(k);
          if (n && (n.kind === "asset" || (st.mode === "photos" && n.kind === "point"))) {
            initCollect();
            gearForKey(k);
          }
        }
        select(st.mode === "collect" ? collectSelected(k) : st.mode === "photos" ? photosSelected(k) : k);
        break;
      case "mode-map":
      case "mode-collect":
      case "mode-photos":
        if (act === "mode-photos" && !photosOk()) break;
        st.mode = act === "mode-collect" ? "collect" : act === "mode-photos" ? "photos" : "map";
        st.sheet = null;
        if (st.mode === "photos" && st.cur) photosSelected(st.cur);
        remember();
        paint();
        break;
      case "wire":
        clOpenWire(k);
        break;
      case "worklist":
        clOpenWorkList();
        break;
      case "fold":
        if (st.fold.has(k)) st.fold.delete(k);
        else st.fold.add(k);
        remember();
        paint();
        break;
      case "focus":
      case "whole":
        st.focus = act === "focus";
        remember();
        paint();
        break;
      case "loads-on":
      case "loads-off":
        st.loads = act === "loads-on";
        remember();
        paint();
        break;
      case "chain-in":
      case "chain-out":
        try {
          window.localStorage.setItem(CHAIN_KEY, String(chainStep(chainScale(), act === "chain-in" ? 1 : -1)));
        } catch {
          // Not remembered. The panel stays its size.
        }
        paint();
        break;
      case "fit": {
        // Fit: the whole diagram across the canvas, never past full size.
        const cv = root.querySelector<HTMLElement>("#mp-canvas");
        const svg = cv?.querySelector("svg");
        const vbw = svg?.viewBox.baseVal.width ?? 0;
        const vbh = svg?.viewBox.baseVal.height ?? 0;
        if (cv && vbw && vbh) st.scale = Math.max(0.35, Math.min(1, (cv.clientWidth - 16) / vbw, (cv.clientHeight - 16) / vbh));
        lastScrolledTo = "";
        paint();
        cv?.scrollTo({ left: 0, top: 0 });
        break;
      }
      case "zoom-in":
      case "zoom-out":
        st.scale = Math.max(0.35, Math.min(1.6, st.scale + (act === "zoom-in" ? 0.15 : -0.15)));
        paint();
        break;
      case "how": {
        const n = tree().nodes.get(k);
        if (n?.linkId) editLink(n.linkId, { "How Known": nextHow(n.how) }, "Map link, how known");
        paint();
        break;
      }
      case "insert":
        openSheet({ kind: "insert", below: k, tab: "floor" });
        break;
      case "next":
        openSheet({ kind: "next", tab: "" });
        break;
      case "new-chain":
        st.cur = null;
        openSheet({ kind: "next", tab: "" });
        break;
      case "tab":
        if (st.sheet && "tab" in st.sheet) st.sheet = { ...st.sheet, tab: v };
        paint();
        break;
      case "cancel":
        closeSheet();
        if (!st.cur) select(st.recent.find((r) => tree().nodes.has(r)) ?? null);
        break;
      case "gaps":
        openSheet({ kind: "gaps" });
        break;
      case "field-find":
        openSheet({ kind: "note", title: "Field Find", text: "Not on the list? Add it in Field Labels and Update Push, or on the Assessment Sheet, print its QR label, then Back To Map and place it.", room: roomHere() });
        break;
      case "add-asset": {
        const room = st.sheet?.kind === "note" && st.sheet.room !== undefined ? st.sheet.room : roomHere();
        closeSheet();
        location.hash = `#/assets/${encodeURIComponent(st.pack.siteNumber)}/${encodeURIComponent(room || "new")}`;
        break;
      }
      case "sheet-scan":
        closeSheet();
        root.querySelector<HTMLElement>('[data-sv="scan"]')?.click();
        break;
      case "pick-source":
        pickSource(v);
        break;
      case "pick-room":
        pickRoom(v);
        break;
      case "pick-asset":
        pickAsset(v);
        break;
      case "pick-point": {
        const step = nextStep(st.reg, tree(), st.cur);
        if (step?.kind === "positions") pickPoint(step.asset, v);
        break;
      }
      case "lugs": {
        const step = nextStep(st.reg, tree(), st.cur);
        if (step?.kind === "breaker") pickPoint(step.asset, "Lugs");
        break;
      }
      case "ldt":
        st.loadType = v === "Non-motor" ? "Non-motor" : "Motor";
        el.parentElement?.querySelectorAll("button").forEach((b) => b.classList.toggle("on", b === el));
        break;
      case "pick-insert":
        pickInsert(v);
        break;
      case "pick-feed":
        pickFeed(v);
        break;
      case "second": {
        const n = st.cur ? tree().nodes.get(st.cur) : undefined;
        if (n && isTransfer(st.reg, n.ref)) openSheet({ kind: "second", asset: n.ref });
        break;
      }
      case "pick-second":
        pickSecond(v);
        break;
      case "separate":
        if (st.cur) openSheet({ kind: "separate", point: st.cur });
        break;
      case "remove":
        removeCur();
        break;
    }
  });
  // The room cards' taps: data-cd (SURVEY_CARDS_C1_WORDS.md).
  // ---- The Drawing: one finger on the paper moves it, two pinch, the wheel zooms (words item 16) ----
  // The paper moves by its transform alone, never a redraw. A drag that moved never counts as a tap.
  const dwPts = new Map<number, { x: number; y: number }>();
  let dwPan: { x: number; y: number; vx: number; vy: number; moved: boolean } | null = null;
  let dwPinch: { d: number; k: number; vx: number; vy: number; cx: number; cy: number } | null = null;
  // A label held: where the finger went down and where the label sat, in grid squares (words item 15).
  let dwHold: { id: string; x: number; y: number; gx: number; gy: number; moving: boolean } | null = null;
  // A line being drawn from a dot, or an end moved by its grip (DRAWING_CONNECTORS_WORDS.md items 2 and 5).
  let dwLine: { from: string; side: Side; grip?: string; end?: "out"; x: number; y: number; moving: boolean } | null = null;
  /** The drawing point under the finger, in drawing units. */
  const dwAt = (ev: PointerEvent): { x: number; y: number } | null => {
    const svg = host?.root.querySelector<SVGSVGElement>("#dw-svg");
    const v = st?.draw.view;
    if (!svg || !v) return null;
    const r = svg.getBoundingClientRect();
    return { x: (ev.clientX - r.left - v.x) / v.k, y: (ev.clientY - r.top - v.y) / v.k };
  };
  /** The label under the finger: on the paper, or a tray label. */
  const dwLabelAt = (ev: PointerEvent): string => {
    const el = document.elementFromPoint(ev.clientX, ev.clientY);
    // A drop on a box's dot counts as the box.
    return el?.closest<HTMLElement | SVGElement>('[data-dw="label"],[data-dw="dot"]')?.dataset.dwV ?? "";
  };
  /** Where a dragged line lands: a box and the side it goes in. A dot gives its side; the box itself, its nearest side. */
  const dwDropAt = (ev: PointerEvent): { id: string; side?: Side } | null => {
    const el = document.elementFromPoint(ev.clientX, ev.clientY);
    const dot = el?.closest<SVGElement>('[data-dw="dot"]');
    if (dot?.dataset.dwV) return { id: dot.dataset.dwV, side: dot.dataset.dwSide as Side };
    const lab = el?.closest<HTMLElement | SVGElement>('[data-dw="label"]');
    const id = lab?.dataset.dwV ?? "";
    if (!id) return null;
    // A tray label is not on the paper: it takes the line the plain way, out the bottom and in the top.
    if (lab instanceof HTMLElement) return { id };
    const at = drawModel().d.labels.find((l) => l.assetId === id);
    const p = dwAt(ev);
    return { id, side: at && p ? nearestSide(at, p) : undefined };
  };
  // A tray label dragged out (Build, BUILD_LOCK_WORDS.md item 5): it stays where it is dropped on the paper, line or not.
  let dwTray: { id: string; ghost: HTMLElement | null; x: number; y: number } | null = null;
  root.addEventListener("pointerdown", (ev) => {
    const t = (ev.target as Element).closest<HTMLElement>('.dw-trlab[data-dw="label"]');
    if (!t || !st) return;
    dwNoTap = false;
    dwTray = { id: t.dataset.dwV ?? "", ghost: null, x: ev.clientX, y: ev.clientY };
  });
  root.addEventListener("pointermove", (ev) => {
    if (!dwTray) return;
    if (!dwTray.ghost) {
      if (Math.abs(ev.clientX - dwTray.x) + Math.abs(ev.clientY - dwTray.y) < 8) return;
      const g = document.createElement("div");
      g.className = "dw-ghost";
      g.textContent = tapeName(dwTray.id);
      document.body.appendChild(g);
      dwTray.ghost = g;
      dwNoTap = true;
    }
    dwTray.ghost.style.left = `${ev.clientX - 30}px`;
    dwTray.ghost.style.top = `${ev.clientY - 16}px`;
  });
  const dwTrayUp = (ev: PointerEvent) => {
    const tr = dwTray;
    dwTray = null;
    if (!tr?.ghost) return;
    tr.ghost.remove();
    if (ev.type !== "pointerup" || !st) return;
    const onPaper = dwSvg(document.elementFromPoint(ev.clientX, ev.clientY));
    const p = onPaper ? dwAt(ev) : null;
    if (!p) return paint();
    // Centered under the finger, snapped to the grid. Undo takes it back to the tray.
    const id = tr.id;
    const gx = Math.round(p.x / GRID - labelWidth(tapeName(id)) / GRID / 2);
    const gy = Math.round(p.y / GRID - 1);
    saveDrawPlace(id, gx, gy, true);
    offerUndo(`${tapeName(id)} placed.`, () => saveDrawPlace(id, gx, gy, false));
  };
  root.addEventListener("pointerup", dwTrayUp);
  root.addEventListener("pointercancel", dwTrayUp);
  let dwNoTap = false;
  const dwSvg = (t: EventTarget | null) => (t instanceof Element ? t.closest<SVGSVGElement>("#dw-svg") : null);
  const dwZoom = (k: number, cx: number, cy: number, from: DrawView) => {
    const kk = Math.min(2.5, Math.max(0.25, k));
    setDrawView({ k: kk, x: cx - ((cx - from.x) * kk) / from.k, y: cy - ((cy - from.y) * kk) / from.k });
  };
  root.addEventListener("pointerdown", (ev) => {
    const svg = dwSvg(ev.target);
    if (!svg || !st?.draw.view) return;
    dwPts.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    dwNoTap = false;
    const v = st.draw.view;
    if (dwPts.size === 2) {
      const [a, b] = [...dwPts.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const r = svg.getBoundingClientRect();
      dwPinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, k: v.k, vx: v.x, vy: v.y, cx: (a.x + b.x) / 2 - r.left, cy: (a.y + b.y) / 2 - r.top };
      dwPan = null;
      if (dwHold?.moving) endDrag(false);
      dwHold = null;
      if (dwLine?.moving && st) {
        st.draw.wire = null;
        paint();
      }
      dwLine = null;
      return;
    }
    // Locked: nothing moves but the paper (BUILD_LOCK_WORDS.md item 11). A press anywhere pans; a tap still opens.
    if (!st.draw.build) {
      dwPan = { x: ev.clientX, y: ev.clientY, vx: v.x, vy: v.y, moved: false };
      return;
    }
    // Build: press on a dot and a new line follows the finger. On a grip: that line's end follows it. On a label: it
    // moves. Anywhere else: the paper moves (DRAWING_CONNECTORS_WORDS.md items 2, 5 and 8).
    const dot = (ev.target as Element).closest<SVGElement>('[data-dw="dot"]');
    if (dot) {
      dwLine = { from: dot.dataset.dwV ?? "", side: (dot.dataset.dwSide as Side) ?? "Bottom", x: ev.clientX, y: ev.clientY, moving: false };
      dwPan = null;
      return;
    }
    const grip = (ev.target as Element).closest<SVGElement>('[data-dw="grip"]');
    const gl = grip ? st.links.find((l) => l.id === grip.dataset.dwV) : undefined;
    if (grip && gl) {
      dwLine = { from: gl.fromAsset, side: "Bottom", grip: gl.id, x: ev.clientX, y: ev.clientY, moving: false };
      dwPan = null;
      return;
    }
    // The leaving end's grip (BUILD_LOCK_WORDS.md item 8): the line hangs from the fed box while the end follows you.
    const gripOut = (ev.target as Element).closest<SVGElement>('[data-dw="grip-out"]');
    const go = gripOut ? st.links.find((l) => l.id === gripOut.dataset.dwV) : undefined;
    if (gripOut && go) {
      dwLine = { from: go.toAsset, side: "Top", grip: go.id, end: "out", x: ev.clientX, y: ev.clientY, moving: false };
      dwPan = null;
      return;
    }
    const lab = (ev.target as Element).closest<SVGGElement>('g[data-dw="label"]');
    const id = lab?.dataset.dwV ?? "";
    const at = id ? drawModel().d.labels.find((l) => l.assetId === id) : undefined;
    if (at) {
      dwHold = { id, x: ev.clientX, y: ev.clientY, gx: at.x / GRID, gy: at.y / GRID, moving: false };
      dwPan = null;
      return;
    }
    dwPan = { x: ev.clientX, y: ev.clientY, vx: v.x, vy: v.y, moved: false };
  });
  /** A drag ends: saved where it was let go, or put back when a second finger turned it into a pinch. */
  const endDrag = (save: boolean) => {
    const d = st?.draw.drag;
    if (!st || !d) return;
    st.draw.drag = null;
    if (save) saveDrawPlace(d.id, d.x, d.y, true);
    paint();
  };
  root.addEventListener("pointermove", (ev) => {
    if (!dwPts.has(ev.pointerId) || !st?.draw.view) return;
    dwPts.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    if (dwPinch && dwPts.size === 2) {
      const [a, b] = [...dwPts.values()] as [{ x: number; y: number }, { x: number; y: number }];
      dwZoom((dwPinch.k * Math.hypot(a.x - b.x, a.y - b.y)) / dwPinch.d, dwPinch.cx, dwPinch.cy, { x: dwPinch.vx, y: dwPinch.vy, k: dwPinch.k });
      dwNoTap = true;
      return;
    }
    if (dwLine) {
      if (!dwLine.moving && Math.abs(ev.clientX - dwLine.x) + Math.abs(ev.clientY - dwLine.y) < 6) return;
      dwLine.moving = true;
      dwNoTap = true;
      const p = dwAt(ev);
      if (p) st.draw.wire = { from: dwLine.from, side: dwLine.side, grip: dwLine.grip, ...(dwLine.end ? { end: dwLine.end } : {}), x: p.x, y: p.y };
      paintDragFrame();
      return;
    }
    if (dwHold) {
      const k = st.draw.view.k;
      const mx = ev.clientX - dwHold.x;
      const my = ev.clientY - dwHold.y;
      if (!dwHold.moving && Math.abs(mx) + Math.abs(my) < 8) return;
      dwHold.moving = true;
      dwNoTap = true;
      // Snaps to the grid as it goes (item 15).
      const x = dwHold.gx + Math.round(mx / (k * GRID));
      const y = dwHold.gy + Math.round(my / (k * GRID));
      const cur = st.draw.drag;
      if (cur && cur.id === dwHold.id && cur.x === x && cur.y === y) return;
      st.draw.drag = { id: dwHold.id, x, y };
      paintDragFrame();
      return;
    }
    if (!dwPan) return;
    const dx = ev.clientX - dwPan.x;
    const dy = ev.clientY - dwPan.y;
    if (!dwPan.moved && Math.abs(dx) + Math.abs(dy) < 8) return;
    if (!dwPan.moved) host?.root.querySelector("#dw-svg")?.classList.add("panning");
    dwPan.moved = true;
    dwNoTap = true;
    setDrawView({ ...st.draw.view, x: dwPan.vx + dx, y: dwPan.vy + dy });
  });
  const dwUp = (ev: PointerEvent) => {
    if (!dwPts.delete(ev.pointerId)) return;
    if (dwPts.size < 2) dwPinch = null;
    if (!dwPts.size) {
      if (dwLine?.moving && st) {
        const ln = dwLine;
        st.draw.wire = null;
        const onto = ev.type === "pointerup" ? dwDropAt(ev) : null;
        if (!onto) paint();
        else if (ln.grip && ln.end === "out") moveStart(ln.grip, onto.id, onto.side ?? "Bottom");
        else if (ln.grip) void moveEnd(ln.grip, onto.id, onto.side ?? "Top");
        else connectDraw(ln.from, onto.id, onto.side ? ln.side : undefined, onto.side);
      }
      dwLine = null;
      if (dwHold?.moving) endDrag(ev.type === "pointerup");
      dwHold = null;
      dwPan = null;
      host?.root.querySelector("#dw-svg")?.classList.remove("panning");
    }
  };
  root.addEventListener("pointerup", dwUp);
  root.addEventListener("pointercancel", dwUp);
  root.addEventListener(
    "wheel",
    (ev) => {
      const svg = dwSvg(ev.target);
      if (!svg || !st?.draw.view) return;
      ev.preventDefault();
      const r = svg.getBoundingClientRect();
      dwZoom(st.draw.view.k * (ev.deltaY < 0 ? 1.1 : 1 / 1.1), ev.clientX - r.left, ev.clientY - r.top, st.draw.view);
    },
    { passive: false },
  );
  // A pan or pinch that ends on a line or a label is not a tap on it. Nor is a tray label dragged out and dropped.
  const dwTrayLab = (t: EventTarget | null) => (t instanceof Element ? t.closest(".dw-trlab") : null);
  root.addEventListener(
    "click",
    (ev) => {
      if (!dwNoTap || !(dwSvg(ev.target) || dwTrayLab(ev.target))) return;
      dwNoTap = false;
      ev.stopPropagation();
      ev.preventDefault();
    },
    true,
  );
  root.addEventListener("click", (ev) => {
    if (!onMap() || !st || st.mode !== "collect") return;
    const el = (ev.target as Element).closest<HTMLElement | SVGElement>("[data-dw]");
    if (!el) return;
    ev.stopPropagation();
    const v = el.dataset.dwV ?? "";
    switch (el.dataset.dw) {
      case "label":
        // Build: taps open nothing (BUILD_LOCK_WORDS.md item 9). Locked: the card opens on the left (item 13).
        if (st.draw.build) return;
        return drawPick(v === st.draw.sel ? null : v);
      case "paper":
        if (st.draw.build && st.draw.line) {
          st.draw.line = null;
          return paint();
        }
        if (!st.draw.build && st.draw.sel) drawPick(null);
        return;
      case "build":
        // One switch (items 1 and 2). Build puts the card away; Done drops the picked line.
        st.draw.build = !st.draw.build;
        st.draw.line = null;
        st.draw.wire = null;
        if (st.draw.build && st.draw.sel) {
          st.draw.sel = null;
          st.cards.open = null;
        }
        host?.root.querySelector("#dw-left")?.scrollTo?.(0, 0);
        return paint();
      case "fit":
        return st.collectView === "oneline" ? fitBox(oneLineNow().box, 0) : fitDrawing();
      case "ol":
        st.draw.olSel = v === st.draw.olSel ? null : v;
        return paint();
      case "ol-paper":
        if (st.draw.olSel) {
          st.draw.olSel = null;
          paint();
        }
        return;
      case "arrange":
        return arrangeDrawing();
      case "line":
        // Build: pick the line, gold with a grip on both ends (BUILD_LOCK_WORDS.md item 8). Locked: the same Wire card
        // the cards use, with Remove Line at its bottom (item 14).
        if (st.draw.build) {
          st.draw.line = v === st.draw.line ? null : v;
          return paint();
        }
        return clOpenWireLink(v, true);
      case "remove-line":
        return removeLine(v);
      case "loads":
        st.loads = !st.loads;
        remember();
        return paint();
      case "point":
        if (st.draw.ask) void finishConnect(st.draw.ask.from, st.draw.ask.to, v, st.draw.ask.side, st.draw.ask.out, st.draw.ask.into);
        return;
      case "ask-bg":
        if (ev.target !== el) return;
        st.draw.ask = null;
        return paint();
      case "ask-cancel":
        st.draw.ask = null;
        return paint();
      case "tray-room": {
        // Open or fold one tray room. Nothing else opens or closes, and the fold holds through every redraw.
        const open = (st.draw.trayOpen ??= new Set(st.cards.room ? [st.cards.room] : []));
        if (open.has(v)) open.delete(v);
        else open.add(v);
        return paint();
      }
    }
  });
  root.addEventListener("click", (ev) => {
    if (!onMap() || !st || st.mode !== "collect") return;
    const el = (ev.target as Element).closest<HTMLElement>("[data-cd]");
    if (!el) return;
    ev.stopPropagation();
    cardsTap(el, ev);
  });
  // Collect (pass 2) taps: data-cl.
  root.addEventListener("click", (ev) => {
    if (!onMap() || !st || st.mode !== "collect") return;
    const el = (ev.target as Element).closest<HTMLElement>("[data-cl]");
    if (!el) return;
    const act = el.dataset.cl;
    const t = el.dataset.clT ?? "";
    const v = el.dataset.clV ?? "";
    if (act === "sheet-bg") {
      if (ev.target === el) clCloseSheet();
      return;
    }
    switch (act) {
      case "sheet-close":
        clCloseSheet();
        break;
      case "nv":
        void clToggleNotVisible(t, el.dataset.clField ?? "");
        break;
      case "opened":
        void clSetOpened(t, v);
        break;
      case "photo":
        clShootOne(t, el.dataset.clPhoto ?? "Nameplate");
        break;
      case "photo-run":
        clPhotoRun(el.dataset.clA ?? "");
        break;
      case "take-photos":
        clTakePhotos(t);
        break;
      case "same":
        void clSameAbove(el.dataset.clA ?? "", Number(v));
        break;
      case "tab":
        clSetTab(v === "wire" ? "wire" : "device");
        break;
      case "row":
        clOpenRow(v);
        break;
      case "row-close":
        clOpenRow(null);
        break;
      case "open-card":
        clOpenCard(el.dataset.clA ?? "");
        break;
      case "photos-toggle":
        clTogglePhotos(el.dataset.clT ?? "");
        break;
      case "wire": {
        const link = el.dataset.clLink ?? "";
        const n = [...tree().nodes.values()].find((x) => x.linkId === link);
        if (n) clOpenWire(n.key);
        else clOpenWireLink(link);
        break;
      }
      case "wire-type":
        void clSetWireType(v === "Busway" ? "Busway" : "Cable");
        break;
      case "wire-last":
        void clSameAsLastWire();
        break;
      case "jump": {
        const key = el.dataset.clK ?? "";
        // The Work List: on the cards, open the card and the bucket or breaker the line names. On the Drawing,
        // pick the gear so what was collected shows on the left.
        clCloseSheet();
        const n = tree().nodes.get(key);
        if (st.collectView === "cards") {
          if (n?.kind === "point") openCardFor(n.asset, n.ref);
          else if (n) openCardFor(n.ref);
        } else if (n) drawPick(n.kind === "point" ? n.asset : n.ref);
        break;
      }
    }
  });
  // The gear front taps: data-gv. Editing is Collect only; Map hands off to its own flow (step 3).
  root.addEventListener("click", (ev) => {
    if (!onMap() || !st) return;
    const el = (ev.target as Element).closest<HTMLElement>("[data-gv]");
    if (!el) return;
    const v = el.dataset.gvV ?? "";
    const editing = st.mode === "collect";
    switch (el.dataset.gv) {
      case "feed-add": {
        // Same as picking a position on the Map: the point goes on the tree and Next asks what it feeds.
        const a = gearAsset();
        if (!a || st.mode !== "map") break;
        clCloseGear();
        pickPoint(a, v);
        openSheet({ kind: "next", tab: "" });
        break;
      }
      case "feed-show": {
        const a = gearAsset();
        const below = a ? feedBelow(collectSite(), a, v) : undefined;
        clCloseGear();
        if (below) select(below.key);
        break;
      }
      case "to-collect":
        st.mode = "collect";
        remember();
        paint();
        break;
      case "open":
        clOpenGear(el.dataset.gvA ?? "");
        break;
      case "done":
        clCloseGear();
        break;
      case "view":
        if (editing) clSetGearView(v === "table" ? "table" : "front");
        break;
      case "pick":
        clPickBucket(v);
        break;
      case "circuits":
        if (editing) void clSetCircuits(v);
        break;
      case "circuits-other":
        if (editing) void clSetCircuits(root.querySelector<HTMLInputElement>("#gv-circ")?.value ?? "");
        break;
      case "circle":
        // In Collect a circle becomes a breaker. In Map and Photos it is only picked.
        if (editing) void clAddBreaker(v);
        else clPickBucket(v);
        break;
      case "poles":
        if (editing) void clSetPoles(Number(v));
        break;
      case "slot":
        if (editing) void clSetSlot(v === "Space" ? "Space" : v === "Breaker" ? "Breaker" : "Removed");
        break;
    }
  });
  // Photos mode taps: data-ph.
  root.addEventListener("click", (ev) => {
    if (!onMap() || !st || st.mode !== "photos") return;
    const el = (ev.target as Element).closest<HTMLElement>("[data-ph]");
    if (!el) return;
    const v = el.dataset.phV ?? "";
    switch (el.dataset.ph) {
      case "retry":
        pvRetry(el.dataset.phPath ?? "");
        break;
      case "shoot":
        clShootOne(el.dataset.phT ?? "", el.dataset.phType ?? "Nameplate");
        break;
      case "row":
        phOpenRow(v);
        break;
      case "row-close":
        phOpenRow(null);
        break;
      case "housing":
        select(photosSelected(assetKey(el.dataset.phA ?? "")));
        break;
      case "collect": {
        const go = phCollectTarget();
        if (!go) break;
        st.mode = "collect";
        remember();
        select(collectSelected(go.key));
        if (go.row) clOpenRow(go.row);
        break;
      }
    }
  });
  // The photo big view, its Delete sheet and Undo: data-pv, in Collect and Photos.
  root.addEventListener("click", (ev) => {
    if (!onMap() || !st || st.mode === "map") return;
    const el = (ev.target as Element).closest<HTMLElement>("[data-pv]");
    if (!el) return;
    switch (el.dataset.pv) {
      case "open":
        pvOpen(el.dataset.pvT ?? "", el.dataset.pvType ?? "");
        break;
      case "done":
        pvDone();
        break;
      case "prev":
        pvStep(-1);
        break;
      case "next":
        pvStep(1);
        break;
      case "share":
        void pvShare();
        break;
      case "retake":
        pvRetake();
        break;
      case "trash":
        pvAskDelete();
        break;
      case "del-yes":
        pvConfirmDelete();
        break;
      case "del-no":
        pvCancelDelete();
        break;
      case "undo":
        pvUndo();
        break;
    }
  });
  root.addEventListener("change", (ev) => {
    const el = ev.target as HTMLSelectElement;
    if (!onMap() || !st || !el.dataset.pvLabel) return;
    void pvRelabel(el.value);
  });
  // Swipe on the big photo steps to the next one.
  let swipeX: number | null = null;
  root.addEventListener(
    "touchstart",
    (ev) => {
      swipeX = st && st.mode !== "map" && pvIsOpen() && (ev.target as Element).closest("#pv-big") ? (ev.touches[0]?.clientX ?? null) : null;
    },
    { passive: true },
  );
  root.addEventListener(
    "touchend",
    (ev) => {
      if (swipeX === null) return;
      const dx = (ev.changedTouches[0]?.clientX ?? swipeX) - swipeX;
      swipeX = null;
      if (Math.abs(dx) > 40) pvStep(dx < 0 ? 1 : -1);
    },
    { passive: true },
  );
  root.addEventListener("change", (ev) => {
    if (!onMap() || !st || st.mode !== "collect") return;
    const el = ev.target as HTMLInputElement | HTMLSelectElement;
    const t = el.dataset.clT ?? "";
    if (el.dataset.clField) void clSetField(t, el.dataset.clField, el.value);
    else if (el.dataset.clOpened) void clSetOpened(t, el.value);
    else if (el.dataset.clReason) void clSetReason(t, el.value);
    else if (el.dataset.clWire) void clSetWire(el.dataset.clWire, el.value);
    else if (el.dataset.clQuick && el.value !== "") void clQuickPick(Number(el.value));
    else if (el.dataset.clLoadhp) void clSetLoadHp(el.dataset.clLoadhp, el.value);
  });
  // A tap into a required field and out again is a look, filled or not (SURVEY_DONE_LOOKED_C1_WORDS.md item 1).
  root.addEventListener("focusout", (ev) => {
    if (!onMap() || !st || st.mode !== "collect") return;
    const el = ev.target as HTMLInputElement | HTMLSelectElement;
    if (!el.dataset?.clField || el.value !== "") return;
    const next = (ev as FocusEvent).relatedTarget as HTMLElement | null;
    void clMarkLooked(el.dataset.clT ?? "", el.dataset.clField, !next?.dataset?.clField);
  });
  root.addEventListener("submit", (ev) => {
    if (!onMap() || !st) return;
    const clForm = (ev.target as HTMLElement).closest<HTMLFormElement>("[data-cl-form]");
    if (clForm) {
      ev.preventDefault();
      void clSubmitOther(root.querySelector<HTMLInputElement>("#cl-other")?.value ?? "");
      return;
    }
    const form = (ev.target as HTMLElement).closest<HTMLFormElement>("[data-mp-form]");
    if (!form) return;
    ev.preventDefault();
    if (form.dataset.mpForm === "load") addLoad();
    if (form.dataset.mpForm === "breaker") {
      const step = nextStep(st.reg, tree(), st.cur);
      const raw = root.querySelector<HTMLInputElement>("#mp-brk")?.value ?? "";
      const point = cleanPoint(raw);
      if (step?.kind !== "breaker") return;
      if (!point || point === "LUGS" || point === "OUT" || point === "SEC") {
        flash("Use letters, numbers and dashes, up to 12. For example 14 or CB-14.");
        return;
      }
      pickPoint(step.asset, point);
    }
  });
}
